/*
  Charmlry Configurator v2 — logic
  Ported from the Claude Design prototype (configurator_monogram.html) and wired
  to live store data. Reads its config from the JSON script tag rendered by
  snippets/configurator-modal.liquid. Writes line item properties in the same
  schema the live customizer uses (color / chain / name / favorite / scratch card /
  Extra charm N, satellites with _parent / _card / _created_at) so fulfillment
  keeps working unchanged.
*/
/* Theme-agnostic event shim (no import map on Dawn) */
const ThemeEvents = { cartUpdate: 'cart:update' };
class CartAddEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = { resource, sourceId, data: Object.assign({}, data) };
  }
}

const FINISHES = [
  { id: 'gold', name: '14k Gold', benefit: 'Warm, classic', grad: 'linear-gradient(135deg,#EFD9A7 0%,#C9A66E 48%,#8A6A2E 100%)', chain: '#C9A66E', letter: 'rgba(255,255,255,.94)', ring: 'rgba(255,255,255,.55)' },
  { id: 'silver', name: 'Silver', benefit: 'Bright, everyday', badge: true, grad: 'linear-gradient(135deg,#F2F4F6 0%,#C9CED4 48%,#8F969E 100%)', chain: '#A8ADB3', letter: '#5E656C', ring: 'rgba(255,255,255,.75)' },
  { id: 'rose', name: 'Rose Gold', benefit: 'Soft pink', grad: 'linear-gradient(135deg,#F2CDBE 0%,#D9A090 48%,#B37A6E 100%)', chain: '#C08A82', letter: 'rgba(255,255,255,.94)', ring: 'rgba(255,255,255,.55)' },
  { id: 'black', name: 'Black', benefit: 'Bold, modern', grad: 'linear-gradient(135deg,#4A4A50 0%,#26262B 48%,#101014 100%)', chain: '#3A3A3E', letter: 'rgba(255,255,255,.85)', ring: 'rgba(255,255,255,.28)' },
];
const MONO = [
  { id: 'initials', who: 'The whole family', label: 'Initials', ex: 'B, L, P, T' },
  { id: 'single', who: 'One special someone', label: 'Single name', ex: 'DAVID' },
  { id: 'two', who: 'The two of you', label: 'Two names', ex: 'SANDY, DALE' },
  { id: 'word', who: 'A word you live by', label: 'A short word', ex: 'Love, Faith' },
];
const STYLES = [
  { id: 'flowing', name: 'Creative Letters', desc: 'Playful & artistic shapes', mode: 'flow', ex: [['K', 'L'], ['A', 'S'], ['M', 'R'], ['J', 'M']] },
  { id: 'stacked', name: 'Vertical Blocks', desc: 'Bold & modern balance', badge: 'Trending', mode: 'stack', ex: [['R', 'T'], ['C', 'H'], ['K', 'B'], ['M', 'F']] },
  { id: 'mirrored', name: 'Mirrored & Heart', desc: 'Romantic & symbolic', mode: 'mirror', ex: [['R'], ['K'], ['M'], ['E']] },
];
const STONE_META = {
  January: { c: '#7A1F2B', meaning: 'Protection' }, February: { c: '#7B4FA0', meaning: 'Peace' },
  March: { c: '#7FB4C7', meaning: 'Serenity' }, April: { c: '#DDE3E8', meaning: 'Strength' },
  May: { c: '#2E7D5B', meaning: 'Hope' }, June: { c: '#EDE6DA', meaning: 'Purity' },
  July: { c: '#A82333', meaning: 'Passion' }, August: { c: '#9BB53C', meaning: 'Prosperity' },
  September: { c: '#2B4C8C', meaning: 'Wisdom' }, October: { c: '#D9CFE8', meaning: 'Creativity' },
  November: { c: '#C98A3B', meaning: 'Warmth' }, December: { c: '#3BA8A0', meaning: 'Luck' },
};
/* Vertical position (% of photo height) of each chain's low point in the
   cfg-length-<handle>.png composition: model shot, 4 chains 16/18/20/22
   from top to bottom. Products reusing the convention must follow the
   same framing. */
const LENGTH_MARKS = { 16: 44.5, 18: 53.5, 20: 61.5, 22: 69.5 };
const CORDS = [
  { id: 'brown', name: 'Brown', c: '#4D3B31' },
  { id: 'navy-blue', name: 'Navy Blue', c: '#273554' },
  { id: 'army-green', name: 'Army Green', c: '#49573A' },
  { id: 'black', name: 'Black', c: '#2E2D2D' },
];
const HELP = {
  material: [
    { t: 'Choose the metal finish for your piece. Each option uses the same design, crafted in a different material and color.' },
    { b: 1, lead: '14k Gold: ', t: 'warm, classic gold tone' }, { b: 1, lead: 'Rose Gold: ', t: 'soft pink hue' },
    { b: 1, lead: 'Silver: ', t: 'bright and timeless' }, { b: 1, lead: 'Black: ', t: 'bold, modern finish' },
  ],
  style: [
    { t: 'Choose the visual style of your monogram. Each has a distinct look, crafted with the same attention to balance.' },
    { b: 1, lead: 'Creative Letters: ', t: 'playful & artistic letter shapes' }, { b: 1, lead: 'Vertical Blocks: ', t: 'bold & modern balance' },
    { b: 1, lead: 'Mirrored & Heart: ', t: 'romantic & symbolic design' },
    { t: "Your designer uses it as a base and refines the final drawing. You review and approve before it's made." },
  ],
  details: [
    { t: 'Optional touches that belong to this piece only. Every piece in your order can have its own.' },
    { t: 'Nothing here is required: continue and nothing is added.' },
  ],
};
const DESIGNERS = [
  { name: 'Valery', rating: 4.9, count: 4812, quote: 'Valery sent the first sketch in a day and refined every detail until it was exactly right. It felt like a friend was designing it.', reviewer: 'Martina R.' },
  { name: 'Kate', rating: 5.0, count: 3967, quote: 'Kate understood what I wanted from one messy note. Three revisions later it was perfect. She never rushed me.', reviewer: 'Jess T.' },
  { name: 'Sofia', rating: 4.8, count: 5394, quote: "Sofia redrew the initials until my grandmother's name felt alive in them. You can tell she genuinely cares.", reviewer: 'Amanda K.' },
];
const STEP_ICONS = {
  material: 'M2.5 5.5 L5 2.5 H10 L12.5 5.5 L7.5 12.5 Z M2.5 5.5 H12.5',
  length: 'M7.5 2.5 V12.5 M5.2 4.6 L7.5 2.3 L9.8 4.6 M5.2 10.4 L7.5 12.7 L9.8 10.4',
  cord: 'M3 7.5 a4.5 4.5 0 1 0 9 0 a4.5 4.5 0 1 0 -9 0 M5.4 7.5 a2.1 2.1 0 1 0 4.2 0 a2.1 2.1 0 1 0 -4.2 0',
  name: 'M4.3 11.5 L7.5 3 L10.7 11.5 M5.5 8.6 H9.5',
  style: 'M7.5 4.8 a3.9 3.9 0 1 0 0.01 0 M6.4 2.4 a1.1 1.1 0 1 0 2.2 0 a1.1 1.1 0 1 0 -2.2 0',
  details: 'M7.5 2.5 L8.5 6.5 L12.5 7.5 L8.5 8.5 L7.5 12.5 L6.5 8.5 L2.5 7.5 L6.5 6.5 Z',
  charms: 'M1.5 5 H13.5 M4.5 5 V7 M4.5 9.5 a1.3 1.3 0 1 0 0.01 0 M10.5 5 V7 M10.5 9.5 a1.3 1.3 0 1 0 0.01 0',
  gift: 'M2.5 6.5 H12.5 V12.5 H2.5 Z M2 4.5 H13 V6.5 H2 Z M7.5 4.5 V12.5 M7.5 4.5 C6.2 2.2 4.2 2.8 4.8 4 M7.5 4.5 C8.8 2.2 10.8 2.8 10.2 4',
};

/* Which experience the page wants: ?customizer=chat|classic wins, else the
   PDP section setting (data-pdp-customizer on the pdp-main root). */
const chatWanted = () => {
  try { const q = new URLSearchParams(window.location.search).get('customizer'); if (q === 'chat' || q === 'classic' || q === 'classicnew') return q === 'chat'; } catch (err) { /* ignore */ }
  const m = document.querySelector('[data-pdp-customizer]');
  return !!m && m.dataset.pdpCustomizer === 'chat';
};
/* New flow under evaluation (proposals of the October A/B review): the classic
   gets its own Gift step and a short review, the chat starts with the first
   choice (no "Ready?"). ?cfg_new=1|0 for previews, else <html data-cfg-new="1">
   (set by ab-customizer.js for the arm that runs it). */
const cfgNewFlow = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get('cfg_new'); if (q === '1' || q === '0') return q === '1';
    if (sp.get('customizer') === 'classicnew') return true;
  } catch (err) { /* ignore */ }
  return document.documentElement.getAttribute('data-cfg-new') === '1';
};
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class ConfiguratorModal extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    const cfgEl = this.querySelector('[data-configurator-config]');
    this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
    if (!this.cfg) return;
    this.KEY = 'charmlry.cfg.v2.' + this.cfg.product.handle;
    this.buildSteps();
    this.state = this.fresh();
    this.root = document.createElement('div');
    this.appendChild(this.root);
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('input', (e) => this.onInput(e));
    this._keydown = (e) => this.onKeydown(e);
    // Fallback trigger binding (works with or without the theme's declarative events)
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[on\\:click="configurator-modal-element/open"]');
      if (t) { e.preventDefault(); this.open(e); }
    });
    // Deep link from the cart's "Edit design": reopen that line's design
    // (in the chat experience when that one is selected on the page).
    const editKey = new URLSearchParams(window.location.search).get('cfg_edit');
    if (editKey && !chatWanted()) setTimeout(() => this.openCartEdit(editKey), 0);
  }

  /* Steps of the classic. Rebuilt at open() when the A/B arm turns the new
     flow on after this element was set up (ab-customizer.js sets
     <html data-cfg-new> and may run after the module scripts). */
  buildSteps() {
    const stepIds = [];
    if (this.cfg.hasCord) stepIds.push('cord');
    stepIds.push('material');
    if (this.cfg.multiCharm) stepIds.push('charms');
    if (this.cfg.hasLength) stepIds.push('length');
    stepIds.push('name', 'style');
    this.newFlow = cfgNewFlow();
    if (this.newFlow) { if (!this.cfg.hideStones) stepIds.push('details'); stepIds.push('gift'); } else stepIds.push('details');
    const STEP_LABELS = { material: 'Finish', details: this.newFlow ? 'Stones' : 'Gift extras', charms: 'Charms', gift: 'Gift' };
    this.STEPS = stepIds.map((id) => ({ id, label: STEP_LABELS[id] || (id.charAt(0).toUpperCase() + id.slice(1)), d: STEP_ICONS[id] }));
  }

  fresh() {
    return {
      screen: this.STEPS[0].id, editReturn: null, finish: this.defaultFinish(this.cfg.finishIds),
      charmCount: 1, names: [],
      length: this.cfg.hasLength ? this.defaultLength(this.cfg.lengths) : null,
      cord: this.cfg.hasCord ? ((this.cfg.cords && this.cfg.cords[0]) || CORDS[0]).id : null,
      nameText: '', pattern: null, style: null, stones: {}, scratch: false, gift: false, done: {},
      helpSheet: null, breakdownOpen: false, exitSheet: false, zoomStyle: null, extraSheet: null,
      pieces: [], designSeq: 4821, designerIdx: Math.floor(Math.random() * DESIGNERS.length),
      warrantyOn: false, warrantyKey: null, inlineEdit: null, dealCelebrated: false, confetti: false,
      mfOffer: null, mf: null, extrasChoice: null, editingPieceIdx: null, toast: null, busy: false, cmPerson: null,
    };
  }

  /* Warm the browser cache for step photos that swap on selection, so the
     first tap on a swatch shows its image instantly. */
  preload(map) {
    this._preloaded = this._preloaded || new Set();
    Object.values(map || {}).forEach((u) => {
      if (u && !this._preloaded.has(u)) {
        this._preloaded.add(u);
        const im = new Image();
        im.src = u;
      }
    });
  }

  /* ---------- lifecycle ---------- */
  open(e) {
    if (e && e.target instanceof HTMLElement) this.triggerEl = e.target;
    if (this.hasAttribute('open')) return;
    if (cfgNewFlow() !== !!this.newFlow) this.buildSteps();
    // Portal: transformed/filtered ancestors (theme scroll animations) trap
    // position:fixed — reparent to <body> so the overlay spans the viewport.
    if (this.parentElement !== document.body) document.body.appendChild(this);
    try {
      if (this._skipRestore) throw new Error('skip');
      const raw = sessionStorage.getItem(this.KEY);
      if (raw) {
        const s = JSON.parse(raw);
        this.state = Object.assign(this.fresh(), s, { toast: null, helpSheet: null, exitSheet: false, zoomStyle: null, extraSheet: null, confetti: false, busy: false });
        if (s.screen !== this.STEPS[0].id || s.nameText) setTimeout(() => this.showToast('Welcome back. We kept your design.'), 350);
      }
    } catch (err) { /* fresh state */ }
    this.setAttribute('open', '');
    document.body.classList.add('cfg-modal-open');
    document.addEventListener('keydown', this._keydown);
    this.render();
    if (this.state.pieces && this.state.pieces.length && !this._skipRestore) this.syncPiecesWithCart();
    this.campStart();
    this.preload(this.cfg.images.finish);
    if (this.cfg.charms && (this.cfg.multiCharm || (this.cfg.offers || []).some((o) => o.multiCharm))) {
      const ci = {}; Object.keys(this.cfg.charms).forEach((k) => { ci[k] = this.cfg.charms[k].image; }); this.preload(ci);
    }
    if (this.cfg.hasLength) this.preload(this.cfg.images.lengthPhotos);
    if (this.cfg.hasCord) {
      this.preload(this.cfg.images.cordImages);
      Object.values(this.cfg.cordCombos || {}).forEach((m) => Object.values(m).forEach((cands) => this.preload({ a: cands[1] })));
    }
  }

  close() {
    clearInterval(this._campTimer);
    this.removeAttribute('open');
    document.body.classList.remove('cfg-modal-open');
    document.removeEventListener('keydown', this._keydown);
    if (this.triggerEl) this.triggerEl.focus();
  }

  /* Reopen a design straight from its cart line (cart "Edit design" link):
     parse the line's order properties back into state, land on the details
     step; saving replaces the old line and its satellites, then returns to
     the cart. */
  async openCartEdit(key) {
    let item = null;
    try {
      const cart = await (await fetch('/cart.js')).json();
      item = (cart.items || []).find((i) => i.key === key) || null;
    } catch (err) { /* treated as not found */ }
    this._skipRestore = true;
    this.open();
    this._skipRestore = false;
    if (!item || item.handle !== this.cfg.product.handle) {
      this.showToast("We couldn't find that design in your cart.");
      return;
    }
    const props = item.properties || {};
    const st = this.fresh();
    const fin = FINISHES.find((f) => f.name === props.color || (props.charm && f.name.replace(/^14k\s*/i, '') === String(props.charm).trim())); if (fin) st.finish = fin.id;
    if (this.cfg.hasLength && props.chain) { const L = parseInt(props.chain, 10); if (L) st.length = L; }
    if (this.cfg.hasCord && props.cord) {
      const c = (this.cfg.cords && this.cfg.cords.length ? this.cfg.cords : CORDS).find((x) => x.name === props.cord);
      if (c) st.cord = c.id;
    }
    st.nameText = props.name || '';
    if (this.cfg.multiCharm) {
      const nm = [];
      for (let k = 1; k <= 8; k++) { if (props['Name ' + k] != null) nm.push(String(props['Name ' + k])); }
      if (nm.length) { st.names = nm; st.charmCount = Math.min(nm.length, this.cfg.maxCharms || 4); this.syncNames(st); }
    }
    const sty = STYLES.find((s) => s.name === props.favorite); if (sty) st.style = sty.id;
    st.scratch = props['scratch card'] === 'yes';
    const stones = {};
    Object.keys(props).forEach((k) => {
      if (k.indexOf('Extra charm') !== 0) return;
      const v = String(props[k]).toLowerCase();
      for (let i = 0; i < 12; i++) {
        const meta = this.stoneMetaAt(i);
        if (String(meta.title).toLowerCase() === v || (meta.month && v.indexOf(meta.month.toLowerCase()) === 0)) {
          stones[i] = (stones[i] || 0) + 1; break;
        }
      }
    });
    st.stones = stones;
    if (props._design_id) { const n = parseInt(String(props._design_id).replace(/\D/g, ''), 10); if (n) st.designSeq = n; }
    const done = {}; this.STEPS.forEach((s) => { done[s.id] = true; });
    st.done = done;
    st.screen = 'review';
    // Coming from the cart's Edit link: open the inline editor right away so
    // the editable fields are immediately visible.
    st.revEditOpen = true;
    st.cartEdit = { key, createdAt: props._created_at || null };
    this.state = st;
    this.render();
    this.showToast('Editing the design from your cart.');
  }

  save() {
    // Cart-edit sessions are transient: never overwrite the shopper's saved
    // in-progress design with the state parsed back from a cart line.
    if (this.state && this.state.cartEdit) return;
    try {
      const s = Object.assign({}, this.state, { toast: null, helpSheet: null, exitSheet: false, zoomStyle: null, extraSheet: null, confetti: false, busy: false, inlineEdit: null });
      sessionStorage.setItem(this.KEY, JSON.stringify(s));
    } catch (err) { /* storage unavailable */ }
  }

  st(patch, cb) {
    const prev = this.state.screen;
    Object.assign(this.state, patch);
    this.save();
    this.render();
    if (patch && patch.screen && patch.screen !== prev) this.emitStep(patch.screen);
    if (cb) cb();
  }

  /* Step reached: consumed by the A/B tracking layer (ab-customizer.js). */
  emitStep(step) {
    try { this.dispatchEvent(new CustomEvent('cfg:step', { bubbles: true, detail: { step: String(step), engine: this.tagName.toLowerCase() } })); } catch (err) { /* ignore */ }
  }

  onKeydown(e) {
    if (e.key !== 'Escape') return;
    const S = this.state;
    if (S.helpSheet || S.zoomStyle || S.extraSheet) this.st({ helpSheet: null, zoomStyle: null, extraSheet: null });
    else if (S.exitSheet) this.st({ exitSheet: false });
    else this.st({ exitSheet: true });
  }

  /* ---------- derived ---------- */
  fmt(cents) {
    const amount = (cents / 100).toFixed(2);
    const f = this.cfg.moneyFormat;
    if (f && f.indexOf('{{') !== -1) return f.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    return '$' + amount;
  }
  charBad(s) { const m = String(s).match(/[^A-Za-z0-9 \-',&+.]/); return m ? m[0] : null; }
  /* ---------- charm bracelet: 1 to 4 charms, one monogram (name) each ----------
     o.charmCount + o.names[]; o.nameText mirrors the names ("Anna, Luca") so
     every recap, header card and preview keeps working unchanged. */
  multiFor(o) { return o === true || (o && o.multiCharm); }
  ctxMulti() { return this.mfMode() ? !!(this.offerOf(this.state.mfOffer) || {}).multiCharm : !!this.cfg.multiCharm; }
  ctxMaxCharms() { const m = this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}).maxCharms : this.cfg.maxCharms; return Math.max(1, m || 4); }
  syncNames(o) {
    const n = Math.max(1, o.charmCount || 1);
    o.names = (o.names || []).slice();
    o.nameText = o.names.slice(0, n).map((x) => String(x || '').trim()).filter(Boolean).join(', ');
    return o;
  }
  charmNameOk(t) { const x = String(t || '').trim(); return x.length > 0 && !this.charBad(x) && !this.tooManyNames(x); }
  multiValid(o) {
    const n = Math.max(1, (o && o.charmCount) || 1);
    for (let k = 0; k < n; k++) if (!this.charmNameOk(((o && o.names) || [])[k])) return false;
    return true;
  }
  charmExtra(o) {
    const n = (o && o.charmCount) || 1;
    if (n <= 1 || !this.cfg.charms) return 0;
    const v = this.cfg.charms[String(n)];
    return v ? v.price : 0;
  }
  /* Live preview of the charm bracelet: one disc per charm along the chain
     (initials of that charm's name), birthstones on the outer slots. */
  charmLine(o, fin) {
    const n = Math.max(1, o.charmCount || 1);
    const XS = { 1: [140], 2: [112, 168], 3: [92, 140, 188], 4: [74, 118, 162, 206] };
    const xs = XS[n] || XS[4];
    const discs = xs.map((x, k) => `<div class="cfg__chain-disc" style="background:${fin.grad};left:${x - 17}px;top:49px"><span class="cfg-serif" style="color:${fin.letter}">${esc(this.initialsOf((o.names || [])[k] || ''))}</span></div>`).join('');
    const lo = xs[0], hi = xs[xs.length - 1];
    return { discs, slots: [lo - 34, hi + 34, lo - 56, hi + 56, lo - 78, hi + 78] };
  }
  charmsLabel(o) { const n = (o && o.charmCount) || 1; return n + (n > 1 ? ' charms' : ' charm'); }
  /* Finishes the piece comes in (the charm bracelet: silver, gold, rose). */
  finishList(ids) {
    const list = ids && ids.length ? FINISHES.filter((f) => ids.indexOf(f.id) !== -1) : FINISHES;
    return list.length ? list : FINISHES;
  }
  ctxFinishIds() { return this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}).finishIds : this.cfg.finishIds; }
  defaultFinish(ids) { const l = this.finishList(ids); return l.some((f) => f.id === 'gold') ? 'gold' : l[0].id; }
  onCharmName() { /* hook: the chat marks an added piece as changed */ }
  nameParts(text) {
    const t = String(text || '').trim(); if (!t) return [];
    let parts = t.split(/[,&+]|\band\b/i).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 1 && parts[0].includes(' ')) { const w = parts[0].split(/\s+/); if (w.length <= 6) parts = w; }
    return parts;
  }
  tokensOf(text) { return this.nameParts(text).slice(0, 8); }
  // Up to 6 names. Single letters are initials and always fit ("B, L, P, T, M, A, R, S").
  tooManyNames(text) { return this.nameParts(text).filter((s) => s.replace(/[^A-Za-z0-9]/g, '').length > 1).length > 6; }
  namesDisplayOf(t) { const k = this.tokensOf(t); return k.length ? k.map((s) => s.toUpperCase()).join(', ') : '…'; }
  initialsOf(t) { const k = this.tokensOf(t); return k.length ? k.map((s) => s[0].toUpperCase()).join('').slice(0, 4) : 'AB'; }
  finishOf(id) { return FINISHES.find((f) => f.id === id) || FINISHES[0]; }
  styleOf(id) { return STYLES.find((s) => s.id === id); }
  cordsFor() {
    const cs = this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}).cords : this.cfg.cords;
    return cs && cs.length ? cs : CORDS;
  }
  cordOf(id, list) { const l = list && list.length ? list : this.cordsFor(); return l.find((c) => c.id === id) || l[0]; }
  mfMode() { return this.state.screen.indexOf('mf-') === 0; }
  lengthsFor() {
    const ls = this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}).lengths : this.cfg.lengths;
    return ls && ls.length ? ls : [16, 18, 20, 22];
  }
  defaultLength(ls) { const l = ls && ls.length ? ls : [16, 18, 20, 22]; return l.indexOf(18) !== -1 ? 18 : l[0]; }
  maxStonesFor() {
    const m = this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}).maxStones : this.cfg.maxStones;
    return m || 6;
  }
  hideStonesFor() { return this.mfMode() ? !!(this.offerOf(this.state.mfOffer) || {}).hideStones : !!this.cfg.hideStones; }
  cur() { return this.mfMode() ? this.state.mf : this.state; }
  setCur(patch, cb) { if (this.mfMode()) { Object.assign(this.state.mf, patch); this.save(); this.render(); if (cb) cb(); } else this.st(patch, cb); }
  stoneCount(o) { const s = (o && o.stones) || {}; return Object.keys(s).reduce((a, k) => a + (s[k] || 0), 0); }
  // What the main piece is called in copy ("your necklace", "your keychain").
  pieceNoun() {
    const t = ((this.cfg.product.title || '') + ' ' + (this.cfg.product.handle || '')).toLowerCase();
    if (t.indexOf('bracelet') !== -1) return 'bracelet';
    if (t.indexOf('keychain') !== -1) return 'keychain';
    if (t.indexOf('charm') !== -1) return 'charm';
    if (t.indexOf('necklace') !== -1) return 'necklace';
    return 'piece';
  }
  /* Each piece has its own stone product (cord bracelets: Crystal Quartz, Moonstone…),
     so names, meanings and cart variants come from that piece's list: t = the offer or
     piece target; without t, the piece on screen (the second piece in mf- screens).
     Index i is the month (all stone products list January → December). */
  stoneCtx() { const S = this.state; return S && S.screen && S.screen.indexOf('mf-') === 0 ? this.offerOf(S.mfOffer) : null; }
  stonesList(t) {
    const c = t === undefined ? this.stoneCtx() : t;
    return (c && c.stones && c.stones.length ? c.stones : null) || this.cfg.stones || [];
  }
  stoneMetaAt(i, t) {
    const s = this.stonesList(t)[i] || {};
    const meta = STONE_META[s.month] || { c: '#C9A66E', meaning: '' };
    // the meaning comes from the stone variant (custom.extra_info), STONE_META only when it's empty
    return Object.assign({}, s, meta, s.meaning ? { meaning: s.meaning } : {});
  }
  /* Stone photo: the cfg-stone-* file, else the stone variant's custom.extra_image
     (keychain charms have no file), else the section/variant image. null = colored dot. */
  stoneSrc(meta, files, imgs) {
    const fail = this._lpFail || new Set();
    const ok = (u) => (u && !fail.has(u) ? u : null);
    return ok((files || {})[meta.month]) || ok((imgs || {})[meta.month]) || ok(meta.image);
  }
  addonPrice(o, ctx) {
    const stonePrice = (ctx && ctx.stonePrice) != null ? ctx.stonePrice : this.cfg.stonePrice;
    return this.charmExtra(o) + this.stoneCount(o) * stonePrice + (o.scratch ? this.cfg.scratch.price : 0) + (o.gift ? this.cfg.gift.price : 0);
  }
  nameValid() {
    if (this.cfg.multiCharm) return this.multiValid(this.state);
    const t = this.state.nameText.trim(); return t.length > 0 && !this.charBad(t) && !this.tooManyNames(t);
  }
  mfNameValid() {
    if ((this.offerOf(this.state.mfOffer) || {}).multiCharm) return this.multiValid(this.state.mf || {});
    const t = ((this.state.mf || {}).nameText || '').trim(); return t.length > 0 && !this.charBad(t) && !this.tooManyNames(t);
  }
  stonesLabel(o, t) {
    const s = (o && o.stones) || {};
    return Object.keys(s).filter((k) => s[k] > 0).sort((a, b) => a - b)
      .map((k) => { const m = this.stoneMetaAt(+k, t); return (m.stone || '') + (s[k] > 1 ? ' ×' + s[k] : ''); }).join(', ');
  }
  designId() { return 'D-' + this.state.designSeq; }
  pieceDesigns() {
    const seen = {}, out = [];
    this.state.pieces.forEach((p) => { const g = p.cfg; if (g && g.designSeq && !seen[g.designSeq]) { seen[g.designSeq] = 1; out.push(g); } });
    return out;
  }
  offerOf(handle) { return (this.cfg.offers || []).find((o) => o.handle === handle); }
  /* New flow, classic only: the second piece also gets "Stones" + "Gift". The
     chat keeps 'mf-details' here and splits it into its own sub-steps (bSteps). */
  splitGift() { return !!this.newFlow && this.localName === 'configurator-modal-element'; }
  mfSteps() {
    const o = this.offerOf(this.state.mfOffer) || {};
    const s = o.multiCharm ? ['mf-charms', 'mf-name'] : ['mf-name'];
    if (!((this.state.mf || {}).designSeq)) s.push('mf-style');
    if (o.hasCord) s.push('mf-cord');
    s.push('mf-finish');
    if (o.hasLength) s.push('mf-length');
    if (this.splitGift()) { if (!o.hideStones) s.push('mf-details'); s.push('mf-gift'); } else s.push('mf-details');
    return s;
  }

  /* Pieces restored from the session must still be in the cart: a shopper who
     removed them there would otherwise see "Added to cart" and bundle prices
     the cart no longer gives. Matched by product and _created_at (a line's key
     changes when its properties change). */
  async syncPiecesWithCart() {
    let cart;
    try { cart = await (await fetch('/cart.js')).json(); } catch (err) { return; }
    const S = this.state;
    const inCart = (p) => {
      const h = p.offerHandle || this.cfg.product.handle;
      const ca = p.cfg && p.cfg.createdAt != null ? String(p.cfg.createdAt) : null;
      return !!ca && (cart.items || []).some((i) => i.handle === h && String((i.properties || {})._created_at) === ca);
    };
    const keep = S.pieces.filter(inCart);
    if (keep.length === S.pieces.length) return;
    if (!keep.some((p) => !p.offerHandle)) this.piecesGone();
    else this.st({ pieces: keep, editingPieceIdx: null });
  }
  /* The first piece left the cart: back to the design review, nothing added. */
  piecesGone() {
    const after = this.state.screen === 'deal' || /^mf-/.test(this.state.screen || '');
    this.st({ pieces: [], editingPieceIdx: null, mfOffer: null, mf: null, dealCelebrated: false, screen: after ? 'review' : this.state.screen });
  }

  /* ---------- toast / confetti ---------- */
  showToast(msg, undoFn) {
    clearTimeout(this._tt);
    this.state.toast = { msg, undo: undoFn || null };
    this.render();
    this._tt = setTimeout(() => { this.state.toast = null; this.render(); }, 4000);
  }

  /* ---------- navigation ---------- */
  goStep(id, fromReview) { this.st({ screen: id, editReturn: fromReview ? 'review' : this.state.editReturn, helpSheet: null, breakdownOpen: false }); }
  next() {
    const i = this.STEPS.findIndex((s) => s.id === this.state.screen);
    const done = Object.assign({}, this.state.done); done[this.state.screen] = true;
    if (this.state.editReturn) { this.st({ done, screen: 'review', editReturn: null }); this.showToast('Saved. Your design is updated.'); return; }
    this.st({ done, screen: i === this.STEPS.length - 1 ? 'review' : this.STEPS[i + 1].id, helpSheet: null });
  }
  back() {
    const i = this.STEPS.findIndex((s) => s.id === this.state.screen);
    if (i > 0) this.st({ screen: this.STEPS[i - 1].id, helpSheet: null });
  }

  /* ---------- cart plumbing ---------- */
  buildItems(cfg, target) {
    // target: {variantId, handle, stoneVariants, hasLength, hasCord}
    const createdAt = String(cfg.createdAt || Date.now());
    const props = {};
    // Cord bracelets use the live personalizer's schema: `cord` + `charm`
    // (finish without the "14k"); every other piece keeps `color`.
    const finishName = this.finishOf(cfg.finish).name;
    if (target.hasCord) props['charm'] = finishName.replace(/^14k\s*/i, '');
    else props['color'] = finishName;
    if (target.hasCord && cfg.cord) props['cord'] = this.cordOf(cfg.cord, target.cords).name;
    if (target.hasLength && cfg.length) props['chain'] = cfg.length + ' inches';
    // Charm bracelet: one "Name N" per charm, as the live personalizer writes them.
    const multi = !!target.multiCharm;
    const charmN = multi ? Math.max(1, cfg.charmCount || 1) : 0;
    if (multi) { for (let k = 0; k < charmN; k++) props['Name ' + (k + 1)] = String((cfg.names || [])[k] || '').trim(); }
    else props['name'] = cfg.nameText.trim();
    const sty = this.styleOf(cfg.style); if (sty) props['favorite'] = sty.name;
    props['scratch card'] = cfg.scratch ? 'yes' : 'no';
    let n = 0;
    const stoneItems = [];
    Object.keys(cfg.stones || {}).sort((a, b) => a - b).forEach((k) => {
      const q = cfg.stones[k]; if (!q) return;
      const meta = this.stoneMetaAt(+k, target.stones ? target : null);
      for (let j = 0; j < q; j++) { n++; props['Extra charm ' + n] = meta.title; }
      const vId = (target.stoneVariants || {})[meta.title];
      if (vId) stoneItems.push({ id: vId, quantity: q, properties: { _parent: target.handle, _created_at: createdAt } });
    });
    props['_created_at'] = createdAt;
    props['_design_id'] = 'D-' + (cfg.designSeq || this.state.designSeq);
    const items = [{ id: target.variantId, quantity: 1, properties: props }];
    // The "charm" product line carries the charm count (variant 1 to 4), linked like the stones.
    const charmV = multi && this.cfg.charms ? this.cfg.charms[String(charmN)] : null;
    if (charmV) items.push({ id: charmV.id, quantity: 1, properties: { _parent: target.handle, _created_at: createdAt } });
    items.push(...stoneItems);
    if (cfg.scratch && this.cfg.scratch.variantId) items.push({ id: this.cfg.scratch.variantId, quantity: 1, properties: { name: cfg.nameText.trim(), _card: target.handle, _created_at: createdAt } });
    // Gift box: no line properties, so Shopify merges repeat adds into one
    // line's quantity (also merges with the cart upsell's plain add).
    if (cfg.gift && this.cfg.gift.variantId) items.push({ id: this.cfg.gift.variantId, quantity: 1 });
    return items;
  }

  async cartAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || err.message || 'Could not add to cart'); }
    const data = await res.json();
    this.notifyCart();
    return (data.items || []).map((i) => i.key);
  }

  async cartRemoveKeys(keys) {
    if (!keys || !keys.length) return;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    await fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    this.notifyCart();
  }

  async notifyCart() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      this.dispatchEvent(new CartAddEvent({}, 'configurator', { source: 'configurator', itemCount: cart.item_count }));
    } catch (err) { /* non-blocking */ }
  }

  mainCfg() {
    const S = this.state;
    return { finish: S.finish, length: S.length, cord: S.cord || null, stones: Object.assign({}, S.stones), scratch: S.scratch, gift: S.gift, nameText: S.nameText, pattern: S.pattern, style: S.style, designSeq: S.designSeq, charmCount: this.cfg.multiCharm ? (S.charmCount || 1) : null, names: this.cfg.multiCharm ? (S.names || []).slice(0, S.charmCount || 1) : null };
  }

  async addToCart() {
    const S = this.state;
    if (S.busy || !this.nameValid() || !S.style) return;
    this.st({ busy: true });
    const cfg = this.mainCfg(); cfg.createdAt = Date.now();
    const target = { variantId: this.cfg.product.variantId, handle: this.cfg.product.handle, stoneVariants: this.cfg.stoneVariants, hasLength: this.cfg.hasLength, hasCord: this.cfg.hasCord, cords: this.cfg.cords, multiCharm: !!this.cfg.multiCharm };
    try {
      if (S.cartEdit) {
        // Replace the edited cart line and its satellites, then go back.
        const cart = await (await fetch('/cart.js')).json();
        const h = this.cfg.product.handle, ca = S.cartEdit.createdAt;
        const doomed = (cart.items || []).filter((i) =>
          i.key === S.cartEdit.key ||
          (ca && i.properties && i.properties._created_at === ca &&
            (i.properties._parent === h || i.properties._card === h))
        ).map((i) => i.key);
        await this.cartRemoveKeys(doomed);
        await this.cartAdd(this.buildItems(cfg, target));
        window.location.href = '/cart';
        return;
      }
      if (S.editingPieceIdx != null) {
        const old = S.pieces[S.editingPieceIdx];
        await this.cartRemoveKeys(old.lineKeys);
        const keys = await this.cartAdd(this.buildItems(cfg, target));
        const pieces = S.pieces.slice();
        pieces[S.editingPieceIdx] = Object.assign({}, old, { cfg, lineKeys: keys, base: this.cfg.product.price });
        this.st({ pieces, screen: 'deal', editingPieceIdx: null, breakdownOpen: false, busy: false }, () => this.showToast('Updated. Your cart reflects the changes.'));
        return;
      }
      const keys = await this.cartAdd(this.buildItems(cfg, target));
      const piece = { kind: this.cfg.product.title, offerHandle: null, base: this.cfg.product.price, cfg, lineKeys: keys, stonePrice: this.cfg.stonePrice };
      const fete = !S.dealCelebrated;
      this.st({ pieces: S.pieces.concat([piece]), screen: 'deal', breakdownOpen: false, done: Object.assign({}, S.done, { details: true }), dealCelebrated: true, confetti: fete, busy: false });
      if (fete) { clearTimeout(this._cfT); this._cfT = setTimeout(() => this.st({ confetti: false }), 3400); }
    } catch (err) {
      this.st({ busy: false });
      this.showToast(err.message || 'Something went wrong. Please try again.');
    }
  }

  startMf(offer) {
    const S = this.state;
    this.preload(offer.finishImages);
    this.preload(offer.cordImages);
    if (offer.hasLength) this.preload(offer.lengthPhotos);
    Object.values(offer.cordCombos || {}).forEach((m) => Object.values(m).forEach((cands) => this.preload({ a: cands[1] })));
    const hasExtras = this.stoneCount(S) > 0 || S.scratch || S.gift;
    this.st({
      mfOffer: offer.handle,
      mf: { nameText: '', pattern: null, style: null, designSeq: null, finish: this.defaultFinish(offer.finishIds), length: 18, cord: ((offer.cords && offer.cords[0]) || CORDS[0]).id, stones: {}, scratch: false, gift: false, charmCount: 1, names: [] },
      extrasChoice: hasExtras ? null : 'choose', screen: offer.multiCharm ? 'mf-charms' : 'mf-name', breakdownOpen: false,
    });
  }
  mfNext() {
    const s = this.mfSteps(); const i = s.indexOf(this.state.screen);
    if (i === -1) { this.st({ screen: this.state.mfStyleReturn || 'mf-finish', mfStyleReturn: null }); return; }
    if (i < s.length - 1) this.st({ screen: s[i + 1] });
  }
  mfBack() {
    const s = this.mfSteps(); const i = s.indexOf(this.state.screen);
    if (i === -1) { this.st({ screen: this.state.mfStyleReturn || 'mf-finish', mfStyleReturn: null }); return; }
    if (i === 0) this.st({ screen: 'deal', mfOffer: null, mf: null, editingPieceIdx: null });
    else this.st({ screen: s[i - 1] });
  }
  async mfAdd() {
    const S = this.state;
    if (S.busy || !this.mfNameValid()) return;
    const offer = this.offerOf(S.mfOffer); if (!offer) return;
    this.st({ busy: true });
    const cfg = Object.assign({}, S.mf); cfg.createdAt = Date.now();
    if (!offer.hasLength) cfg.length = null;
    if (!offer.hasCord) cfg.cord = null;
    if (!cfg.designSeq) { const maxSeq = S.pieces.reduce((a, p) => Math.max(a, (p.cfg && p.cfg.designSeq) || 0), S.designSeq); cfg.designSeq = maxSeq + 1; }
    if (!cfg.style) cfg.style = S.style || 'flowing';
    const target = { variantId: offer.variantId, handle: offer.handle, stoneVariants: offer.stoneVariants, stones: offer.stones, hasLength: offer.hasLength, hasCord: offer.hasCord, cords: offer.cords, multiCharm: !!offer.multiCharm };
    if (offer.multiCharm) this.syncNames(cfg); else { cfg.charmCount = null; cfg.names = null; }
    try {
      if (S.editingPieceIdx != null) {
        const old = S.pieces[S.editingPieceIdx];
        await this.cartRemoveKeys(old.lineKeys);
        const keys = await this.cartAdd(this.buildItems(cfg, target));
        const pieces = S.pieces.slice();
        pieces[S.editingPieceIdx] = Object.assign({}, old, { cfg, lineKeys: keys });
        this.st({ pieces, mfOffer: null, mf: null, screen: 'deal', editingPieceIdx: null, breakdownOpen: false, busy: false }, () => this.showToast('Updated. Your cart reflects the changes.'));
        return;
      }
      const keys = await this.cartAdd(this.buildItems(cfg, target));
      const piece = { kind: offer.title, offerHandle: offer.handle, base: offer.price, cfg, lineKeys: keys, stonePrice: offer.stonePrice };
      this.st({ pieces: S.pieces.concat([piece]), mfOffer: null, mf: null, screen: 'deal', breakdownOpen: false, busy: false },
        () => this.showToast('Added at the bundle price.'));
    } catch (err) {
      this.st({ busy: false });
      this.showToast(err.message || 'Something went wrong. Please try again.');
    }
  }

  pieceTarget(piece) {
    if (!piece.offerHandle) {
      return { variantId: this.cfg.product.variantId, handle: this.cfg.product.handle, stoneVariants: this.cfg.stoneVariants, hasLength: this.cfg.hasLength, hasCord: this.cfg.hasCord, base: this.cfg.product.price, stonePrice: this.cfg.stonePrice, maxStones: this.cfg.maxStones, hideStones: this.cfg.hideStones, lengths: this.cfg.lengths, cords: this.cfg.cords, stoneFiles: this.cfg.stoneFiles, stoneImages: this.cfg.stoneImages, multiCharm: !!this.cfg.multiCharm, maxCharms: this.cfg.maxCharms, finishIds: this.cfg.finishIds };
    }
    const o = this.offerOf(piece.offerHandle);
    if (!o) return null;
    return { variantId: o.variantId, handle: o.handle, stoneVariants: o.stoneVariants, hasLength: o.hasLength, hasCord: o.hasCord, base: o.price, stonePrice: o.stonePrice, maxStones: o.maxStones, hideStones: o.hideStones, lengths: o.lengths, cords: o.cords, stoneFiles: o.stoneFiles, stoneImages: o.stoneImages, stones: o.stones, multiCharm: !!o.multiCharm, maxCharms: o.maxCharms, finishIds: o.finishIds };
  }

  ieValid() {
    const ie = this.state.inlineEdit;
    if (!ie) return false;
    const pc = this.state.pieces[ie.idx];
    if (pc && (this.pieceTarget(pc) || {}).multiCharm) return this.multiValid(ie.cfg) && !!ie.cfg.style;
    const t = (ie.cfg.nameText || '').trim();
    return t.length > 0 && !this.charBad(t) && !this.tooManyNames(t) && !!ie.cfg.style;
  }

  async saveInlineEdit() {
    const S = this.state, ie = S.inlineEdit;
    if (!ie || S.busy || !this.ieValid()) return;
    const piece = S.pieces[ie.idx];
    const target = piece && this.pieceTarget(piece);
    if (!target) { this.showToast('Could not update this piece. Try again.'); return; }
    const cfg = Object.assign({}, ie.cfg);
    const old = piece.cfg;
    if (target.multiCharm) this.syncNames(cfg);
    const designChanged = cfg.nameText !== old.nameText || cfg.style !== old.style;
    const shared = S.pieces.some((p, j) => j !== ie.idx && p.cfg && p.cfg.designSeq === old.designSeq);
    if (designChanged && shared) {
      const maxSeq = S.pieces.reduce((a, p) => Math.max(a, (p.cfg && p.cfg.designSeq) || 0), S.designSeq);
      cfg.designSeq = maxSeq + 1;
    }
    cfg.createdAt = Date.now();
    this.st({ busy: true });
    try {
      await this.cartRemoveKeys(piece.lineKeys);
      const keys = await this.cartAdd(this.buildItems(cfg, target));
      const pieces = S.pieces.slice();
      pieces[ie.idx] = Object.assign({}, piece, { cfg, lineKeys: keys });
      this.st({ pieces, inlineEdit: null, busy: false }, () => this.showToast('Updated. Your cart reflects the changes.'));
    } catch (err) {
      this.st({ busy: false });
      this.showToast(err.message || 'Something went wrong. Please try again.');
    }
  }

  ieMsgsHtml() {
    const ie = this.state.inlineEdit;
    if (!ie) return '';
    const pcM = this.state.pieces[ie.idx];
    if (pcM && (this.pieceTarget(pcM) || {}).multiCharm) return this.charmMsgsHtml(ie.cfg);
    const t = (ie.cfg.nameText || '').trim();
    const bad = this.charBad(t);
    if (bad) return `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>Letters, numbers, spaces, hyphens and apostrophes only. Remove "${esc(bad)}" to continue.</span></div>`;
    if (this.tooManyNames(t)) return `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>That's more than 6 names. Keep it to 6, or use initials.</span></div>`;
    return '';
  }

  syncInline() {
    const msgs = this.root.querySelector('[data-region="ie-msgs"]');
    if (msgs) msgs.innerHTML = this.ieMsgsHtml();
    this.root.querySelectorAll('[data-input="ie-cname"]').forEach((ce) => {
      const t = ce.value.trim();
      ce.classList.toggle('cfg__input--invalid', !!t && !this.charmNameOk(t));
    });
    const inp = this.root.querySelector('[data-input="ie-name"]');
    if (inp) {
      const t = (this.state.inlineEdit && this.state.inlineEdit.cfg.nameText || '').trim();
      inp.classList.toggle('cfg__input--invalid', !!t && (!!this.charBad(t) || this.tooManyNames(t)));
    }
    const save = this.root.querySelector('[data-action="ie-save"]');
    if (save) save.disabled = !this.ieValid();
  }

  inlineEditorHtml() {
    const S = this.state, ie = S.inlineEdit;
    if (!ie || !S.pieces[ie.idx]) return '';
    const piece = S.pieces[ie.idx];
    const target = this.pieceTarget(piece);
    if (!target) return '';
    const c = ie.cfg;
    const stones = this.stoneCardsHtml(c, { mini: true, stonePrice: target.stonePrice, prefix: 'ie-stone', stoneFiles: target.stoneFiles, stoneImages: target.stoneImages, stones: target.stones || null });
    const unit = target.base + this.addonPrice(c, { stonePrice: target.stonePrice });
    return `<div class="cfg__inline-editor">
      ${target.multiCharm ? this.charmFieldsHtml(c, 'ie-cname', 'ie-charms', target.maxCharms) : `<div class="cfg__rev-field"><span class="cfg__rev-label">NAME</span>
        <input class="cfg__input cfg__input--small" data-input="ie-name" value="${esc(c.nameText || '')}" autocomplete="off">
        <div data-region="ie-msgs">${this.ieMsgsHtml()}</div>
      </div>`}
      <div class="cfg__rev-field"><span class="cfg__rev-label">STYLE</span>
        <div class="cfg__rev-row">${STYLES.map((st) => `<button class="cfg__rev-chip cfg__rev-chip--style" data-on="${c.style === st.id}" data-action="ie-style" data-id="${st.id}" aria-label="${st.name}${c.style === st.id ? ', selected' : ''}">${this.styleThumbHtml(st, c.finish)}<span>${st.name}</span></button>`).join('')}</div>
      </div>
      <div class="cfg__rev-field"><span class="cfg__rev-label">FINISH</span>
        <div class="cfg__rev-swatches">${this.finishList(target.finishIds).map((f) => `<button class="cfg__rev-swatch" data-on="${c.finish === f.id}" data-action="ie-finish" data-id="${f.id}" style="background:${f.grad}" aria-label="${f.name}${c.finish === f.id ? ', selected' : ''}"></button>`).join('')}</div>
      </div>
      ${target.hasCord ? `<div class="cfg__rev-field"><span class="cfg__rev-label">CORD</span>
        <div class="cfg__rev-swatches">${(target.cords && target.cords.length ? target.cords : CORDS).map((cd) => `<button class="cfg__rev-swatch" data-on="${c.cord === cd.id}" data-action="ie-cord" data-id="${cd.id}" style="background:${cd.c}" aria-label="${cd.name} cord${c.cord === cd.id ? ', selected' : ''}"></button>`).join('')}</div>
      </div>` : ''}
      ${target.hasLength ? `<div class="cfg__rev-field"><span class="cfg__rev-label">LENGTH</span>
        <div class="cfg__rev-row">${(target.lengths && target.lengths.length ? target.lengths : [16, 18, 20, 22]).map((L) => `<button class="cfg__rev-chip cfg__rev-chip--len" data-on="${c.length === L}" data-action="ie-length" data-n="${L}" aria-label="${L} inches${c.length === L ? ', selected' : ''}">${L}″</button>`).join('')}</div>
      </div>` : ''}
      ${target.hideStones ? '' : `<div class="cfg__rev-field">
        <div class="cfg__section-label-row"><span class="cfg__rev-label" style="flex:1">BIRTHSTONES</span><span style="font-size:12px;color:#6C6A6B">${this.stoneCount(c)} of ${target.maxStones || 6}</span></div>
        <div class="cfg__stones-row" style="padding:2px;margin:0 -2px">${stones}</div>
      </div>`}
      <div class="cfg__rev-row">
        <button class="cfg__rev-chip" data-on="${!!c.scratch}" data-action="ie-scratch" style="font-size:13px">Scratch card · ${this.fmt(this.cfg.scratch.price)}</button>
        <button class="cfg__rev-chip" data-on="${!!c.gift}" data-action="ie-gift" style="font-size:13px">Gift box · ${this.fmt(this.cfg.gift.price)}</button>
      </div>
      <div class="cfg__inline-foot">
        <span class="cfg__inline-price">Unit price ${this.fmt(unit)}</span>
        <button class="cfg__btn-ghost" data-action="ie-cancel">Cancel</button>
        <button class="cfg__btn-solid" data-action="ie-save" ${this.ieValid() && !S.busy ? '' : 'disabled'}>${S.busy ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>`;
  }

  editPiece(i) {
    const p = this.state.pieces[i]; if (!p) return;
    const cfg = p.cfg || {};
    if (!p.offerHandle) {
      const doneAll = {}; this.STEPS.forEach((s) => { doneAll[s.id] = true; });
      this.st({
        finish: cfg.finish || 'gold', length: cfg.length || (this.cfg.hasLength ? this.defaultLength(this.cfg.lengths) : null),
        cord: cfg.cord || (this.cfg.hasCord ? ((this.cfg.cords && this.cfg.cords[0]) || CORDS[0]).id : null),
        nameText: cfg.nameText || '', pattern: cfg.pattern || null, style: cfg.style || null,
        stones: Object.assign({}, cfg.stones || {}), scratch: !!cfg.scratch, gift: !!cfg.gift,
        designSeq: cfg.designSeq || this.state.designSeq,
        charmCount: cfg.charmCount || 1, names: (cfg.names || []).slice(),
        done: doneAll, screen: 'review', editingPieceIdx: i, breakdownOpen: false, inlineEdit: null,
      });
    } else {
      const offer = this.offerOf(p.offerHandle);
      if (offer) { this.preload(offer.finishImages); this.preload(offer.cordImages); if (offer.hasLength) this.preload(offer.lengthPhotos); }
      this.st({
        mfOffer: p.offerHandle,
        mf: { nameText: cfg.nameText || '', pattern: cfg.pattern || null, style: cfg.style || null, designSeq: cfg.designSeq || null, finish: cfg.finish || 'gold', length: cfg.length || 18, cord: cfg.cord || 'black', stones: Object.assign({}, cfg.stones || {}), scratch: !!cfg.scratch, gift: !!cfg.gift, charmCount: cfg.charmCount || 1, names: (cfg.names || []).slice() },
        extrasChoice: 'choose', editingPieceIdx: i, screen: offer && offer.multiCharm ? 'mf-charms' : 'mf-name', breakdownOpen: false, inlineEdit: null,
      });
    }
  }

  /* ---------- events ---------- */
  onClick(e) {
    const el = e.target.closest('[data-action]');
    if (!el || !this.contains(el)) return;
    const a = el.dataset.action;
    const i = el.dataset.i != null ? +el.dataset.i : null;
    const S = this.state;
    const actions = {
      close: () => this.st({ exitSheet: true }),
      backdrop: () => { if (e.target === el) this.st({ exitSheet: true }); },
      'sheet-backdrop': () => { if (e.target === el) this.st({ helpSheet: null, exitSheet: false, zoomStyle: null, extraSheet: null }); },
      'close-sheets': () => this.st({ helpSheet: null, exitSheet: false, zoomStyle: null, extraSheet: null }),
      'keep-exit': () => { this.save(); this.st({ exitSheet: false }); this.close(); },
      'discard-exit': () => {
        try { sessionStorage.removeItem(this.KEY); } catch (err) { }
        this.state = this.fresh(); this.render(); this.close();
      },
      'go-step': () => { const id = el.dataset.step; if (S.done[id] && S.screen !== id) this.goStep(id, S.screen === 'review'); },
      back: () => this.back(),
      next: () => this.next(),
      'primary-review': () => this.addToCart(),
      'header-back': () => {
        if (S.editingPieceIdx != null) this.st({ screen: 'deal', editingPieceIdx: null });
        else this.st({ screen: this.STEPS[this.STEPS.length - 1].id });
      },
      help: () => this.st({ helpSheet: el.dataset.help, helpTab: 0 }),
      'help-tab': () => this.st({ helpTab: +el.dataset.tab }),
      'cm-person': () => this.st({ cmPerson: S.cmPerson === el.dataset.p ? null : el.dataset.p }),
      'select-finish': () => this.setCur({ finish: el.dataset.id }),
      'select-charms': () => { const o = this.cur(); o.charmCount = Math.max(1, Math.min(this.ctxMaxCharms(), +el.dataset.n || 1)); this.syncNames(o); if (this.mfMode()) o.designSeq = null; this.setCur({}); },
      'ie-charms': () => { const c = S.inlineEdit.cfg; c.charmCount = Math.max(1, Math.min(4, +el.dataset.n || 1)); this.syncNames(c); this.st({}); },
      'select-length': () => this.setCur({ length: +el.dataset.n }),
      'select-cord': () => this.setCur({ cord: el.dataset.id }),
      'select-pattern': () => this.st({ pattern: S.pattern === el.dataset.id ? null : el.dataset.id }),
      'select-style': () => this.setCur({ style: el.dataset.id }),
      'zoom-style': () => { e.stopPropagation(); this.st({ zoomStyle: el.dataset.id }); },
      'zoom-pick': () => { this.setCur({ style: S.zoomStyle }); this.st({ zoomStyle: null }); },
      'stone-inc': () => {
        const mxS = this.maxStonesFor();
        if (this.stoneCount(this.cur()) >= mxS) { this.showToast('Up to ' + mxS + ' stones per piece.'); return; }
        const ns = Object.assign({}, this.cur().stones); ns[i] = (ns[i] || 0) + 1; this.setCur({ stones: ns });
      },
      'stone-dec': () => {
        const cs = this.cur().stones || {}; if (!cs[i]) return;
        const ns = Object.assign({}, cs); if (ns[i] <= 1) delete ns[i]; else ns[i] -= 1; this.setCur({ stones: ns });
      },
      'toggle-scratch': () => {
        const on = !!this.cur().scratch;
        if (on) this.setCur({ scratch: false }, () => this.showToast('Removed.', () => this.setCur({ scratch: true }, () => { this.state.toast = null; this.render(); })));
        else this.setCur({ scratch: true });
      },
      'toggle-gift': () => {
        const on = !!this.cur().gift;
        if (on) this.setCur({ gift: false }, () => this.showToast('Removed.', () => this.setCur({ gift: true }, () => { this.state.toast = null; this.render(); })));
        else this.setCur({ gift: true });
      },
      'extra-detail': () => this.st({ extraSheet: el.dataset.id }),
      'extra-sheet-toggle': () => {
        const id = S.extraSheet;
        if (id === 'scratch') this.setCur({ scratch: !this.cur().scratch });
        else this.setCur({ gift: !this.cur().gift });
      },
      'toggle-how': () => this.st({ howOpen: !S.howOpen }),
      'toggle-rev-edit': () => this.st({ revEditOpen: !S.revEditOpen }),
      'toggle-breakdown': () => this.st({ breakdownOpen: !S.breakdownOpen }),
      'toast-undo': () => { if (S.toast && S.toast.undo) S.toast.undo(); },
      'copy-extras': () => this.st({ mf: Object.assign({}, S.mf, { stones: Object.assign({}, S.stones), scratch: S.scratch, gift: S.gift }), extrasChoice: 'copied' }),
      'choose-extras': () => this.st({ extrasChoice: 'choose' }),
      'use-design': () => {
        const d = this.pieceDesigns()[i];
        if (d) this.st({ mf: Object.assign({}, S.mf, { nameText: d.nameText, pattern: d.pattern, style: d.style, designSeq: d.designSeq }) });
      },
      'use-name': () => {
        const d = this.pieceDesigns().filter((x) => x.designSeq !== S.designSeq)[i];
        if (d) this.st({ nameText: d.nameText, pattern: d.pattern });
      },
      'ask-new-design': () => this.st({ screen: 'mf-style', mfStyleReturn: S.screen }),
      'mf-back': () => this.mfBack(),
      'mf-next': () => this.mfNext(),
      'mf-add': () => this.mfAdd(),
      'start-mf': () => this.startMf(this.cfg.offers[i]),
      'edit-piece': () => {
        const open = S.inlineEdit && S.inlineEdit.idx === i;
        const pc = S.pieces[i];
        this.st({ inlineEdit: open || !pc ? null : { idx: i, cfg: JSON.parse(JSON.stringify(pc.cfg)) } });
      },
      'ie-style': () => { S.inlineEdit.cfg.style = el.dataset.id; this.st({}); },
      'ie-finish': () => { S.inlineEdit.cfg.finish = el.dataset.id; this.st({}); },
      'ie-cord': () => { S.inlineEdit.cfg.cord = el.dataset.id; this.st({}); },
      'ie-length': () => { S.inlineEdit.cfg.length = +el.dataset.n; this.st({}); },
      'ie-stone-inc': () => {
        const c = S.inlineEdit.cfg;
        const ieT = this.pieceTarget(S.pieces[S.inlineEdit.idx]) || {};
        const ieMx = ieT.maxStones || 6;
        if (this.stoneCount(c) >= ieMx) { this.showToast('Up to ' + ieMx + ' stones per piece.'); return; }
        c.stones = Object.assign({}, c.stones); c.stones[i] = (c.stones[i] || 0) + 1; this.st({});
      },
      'ie-stone-dec': () => {
        const c = S.inlineEdit.cfg;
        if (!c.stones || !c.stones[i]) return;
        c.stones = Object.assign({}, c.stones);
        if (c.stones[i] <= 1) delete c.stones[i]; else c.stones[i] -= 1;
        this.st({});
      },
      'ie-scratch': () => { S.inlineEdit.cfg.scratch = !S.inlineEdit.cfg.scratch; this.st({}); },
      'ie-gift': () => { S.inlineEdit.cfg.gift = !S.inlineEdit.cfg.gift; this.st({}); },
      'ie-cancel': () => this.st({ inlineEdit: null }),
      'ie-save': () => this.saveInlineEdit(),
      'go-cart': () => { this.save(); window.location.href = '/cart'; },
    };
    if (actions[a]) actions[a]();
  }

  onInput(e) {
    const el = e.target;
    if (!el.dataset || !el.dataset.input) return;
    const v = el.value;
    const ci = el.dataset.input;
    if (ci === 'cname' || ci === 'mf-cname' || ci === 'ie-cname') {
      const o = ci === 'cname' ? this.state : ci === 'mf-cname' ? this.state.mf : (this.state.inlineEdit || {}).cfg;
      if (!o) return;
      const k = +el.dataset.k || 0;
      o.names = (o.names || []).slice(); o.names[k] = v;
      this.syncNames(o);
      if (ci === 'mf-cname') o.designSeq = null;
      this.onCharmName(ci);
      this.save();
      if (ci === 'ie-cname') this.syncInline(); else this.syncDynamic();
      return;
    }
    if (el.dataset.input === 'name') this.state.nameText = v;
    if (el.dataset.input === 'mf-name') { Object.assign(this.state.mf, { nameText: v, designSeq: null }); }
    if (el.dataset.input === 'ie-name') {
      if (this.state.inlineEdit) this.state.inlineEdit.cfg.nameText = v;
      this.save();
      this.syncInline();
      return;
    }
    this.save();
    this.syncDynamic();
  }

  /* ---------- partial sync (keeps input focus) ---------- */
  syncDynamic() {
    const msg = this.root.querySelector('[data-region="name-msgs"]');
    if (msg) msg.innerHTML = this.nameMsgsHtml();
    // mf-name: typing a new name clears the saved-design selection — reflect it live
    if (this.state.screen === 'mf-name') {
      const seq = (this.state.mf || {}).designSeq;
      const designs = this.pieceDesigns();
      this.root.querySelectorAll('[data-action="use-design"]').forEach((row) => {
        const d = designs[+row.dataset.i];
        const sel = !!seq && !!d && d.designSeq === seq;
        row.dataset.selected = sel;
        const radio = row.querySelector('.cfg__radio');
        if (radio) {
          radio.classList.toggle('cfg__radio--on', sel);
          radio.innerHTML = sel ? '<span class="cfg__radio-dot"></span>' : '';
        }
      });
    }
    this.root.querySelectorAll('[data-input="cname"],[data-input="mf-cname"]').forEach((ce) => {
      const t = ce.value.trim();
      ce.classList.toggle('cfg__input--invalid', !!t && !this.charmNameOk(t));
    });
    const inp = this.root.querySelector('[data-input="name"],[data-input="mf-name"]');
    if (inp) {
      const bad = this.mfMode() ? !this.mfNameValid() && (this.state.mf.nameText || '').trim() : !this.nameValid() && this.state.nameText.trim();
      inp.classList.toggle('cfg__input--invalid', !!bad);
    }
    const foot = this.root.querySelector('[data-region="footer"]');
    if (foot) foot.innerHTML = this.footerHtml();
  }

  /* ---------- seasonal campaign (sections/customizer-campaign.liquid) ----------
     The header-group section prints #cm-campaign: mode, Christmas cut-off and
     one entry per "communication" (dates as epoch seconds, texts). The active
     one is decided here, in the browser, so the switch happens at the exact
     minute. ?campaign=N previews block N, ?campaign=off hides every campaign.
     No campaign = both customizers render exactly as before. */
  campaignData() {
    if (this._campData === undefined) {
      const el = document.getElementById('cm-campaign');
      try { this._campData = el ? JSON.parse(el.textContent) : null; } catch (err) { this._campData = null; }
    }
    return this._campData;
  }

  campaign() {
    const d = this.campaignData();
    if (!d || !Array.isArray(d.comms) || !d.comms.length) return null;
    let q = null;
    try { q = new URLSearchParams(window.location.search).get('campaign'); } catch (err) { /* ignore */ }
    if (q === 'off') return null;
    if (q && d.comms[parseInt(q, 10) - 1]) return d.comms[parseInt(q, 10) - 1];
    if (d.mode === 'off') return null;
    const now = Date.now() / 1000;
    let c = null;
    d.comms.forEach((x) => {
      if (x.start && x.end && now >= x.start && now < x.end && (!c || x.end - x.start < c.end - c.start)) c = x;
    });
    if (!c && d.mode === 'on') c = d.comms[0];
    return c;
  }

  /* Editor text -> safe HTML: **bold**, line breaks and the placeholders
     [date] [n] [arrival] [extra_price] [price] [save] [end_date] [ends]. */
  campText(t, c) {
    if (!t) return '';
    const d = this.campaignData() || {};
    const now = Date.now();
    const dayOf = (ms) => { const x = new Date(ms); x.setHours(0, 0, 0, 0); return x.getTime(); };
    // Cut-off day as the shop wrote it (YYYY-MM-DD), counted on the
    // visitor's calendar: "66 days left" means 66 days to that date.
    const cd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d.cutoffDay || '');
    const cut = cd ? new Date(+cd[1], +cd[2] - 1, +cd[3]).getTime() : 0;
    const b = this.cfg.bundle || {};
    const map = {
      date: d.cutoffLabel || '',
      n: cut ? String(Math.max(0, Math.round((cut - dayOf(now)) / 864e5))) : '',
      arrival: new Date(now + (+d.arrivalDays || 20) * 864e5).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      extra_price: this.fmt(b.secondPrice),
      price: this.fmt(this.cfg.product.price),
      save: this.fmt(b.savings),
      end_date: (c && c.endLabel) || '',
    };
    return esc(t)
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/\r?\n/g, '<br>')
      .replace(/\[(date|n|arrival|extra_price|price|save|end_date)\]/g, (m, k) => esc(map[k]))
      .replace(/\[ends\]/g, () => this.campClockHtml(c));
  }

  /* Look classes: light, dark (Charcoal + Charmlry Gold) or deal (dark
     layout in navy and orange, the colors shoppers know from big deal days). */
  campDark(c) { return !!c && (c.look === 'dark' || c.look === 'deal'); }
  campLookCls(c) { return c && c.look === 'deal' ? 'cfg-cm--dark cfg-cm--deal' : this.campDark(c) ? 'cfg-cm--dark' : 'cfg-cm--light'; }

  campClockHtml(c) {
    return c && c.end ? `<span class="cfg-cm__clock" data-cm-clock="${c.end}">${esc(this.campLeft(c.end))}</span>` : '';
  }

  campLeft(end) {
    const s = Math.max(0, Math.floor(end - Date.now() / 1000));
    const dd = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    return dd ? `${dd}d ${h}h ${m}m` : `${h}h ${m}m`;
  }

  /* While the customizer is open: keep the countdowns ticking, and re-render
     when the active communication changes (a deal ending mid-session). */
  campStart() {
    clearInterval(this._campTimer);
    if (!this.campaignData()) return;
    this._campId = (this.campaign() || {}).id || null;
    this._campTimer = setInterval(() => {
      if (!this.hasAttribute('open')) { clearInterval(this._campTimer); return; }
      const id = (this.campaign() || {}).id || null;
      if (id !== this._campId) { this._campId = id; this.render(); return; }
      this.querySelectorAll('[data-cm-clock]').forEach((el) => { el.textContent = this.campLeft(+el.dataset.cmClock); });
    }, 15000);
  }

  /* Classic: the ribbon under the stepper (every screen but the deal). */
  campRibbonHtml() {
    // A running promo countdown (sections/promo-countdown.liquid) takes the ribbon's place.
    const promo = window.CharmlryPromo && window.CharmlryPromo.blockHtml();
    if (promo) return promo;
    const c = this.campaign();
    if (!c || !c.ribbon) return '';
    return `<div class="cfg-cm-ribbon ${this.campLookCls(c)}">
      <span class="cfg-cm-ribbon__icon" aria-hidden="true">${this.campSparkSvg(14)}</span>
      <span class="cfg-cm-ribbon__text">${c.kicker ? `<span class="cfg-cm__kicker">${esc(c.kicker)}</span>` : ''}<span>${this.campText(c.ribbon, c)}</span></span>
    </div>`;
  }

  // Campaign kicker: the part after the first ' · ' hides on small phones (CSS), e.g. 'Holiday gifting · Start early'.
  campKicker(k) {
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const i = String(k).indexOf(' · ');
    return i < 0 ? esc(k) : esc(k.slice(0, i)) + '<span class="cfg-cm__kicker-more">' + esc(k.slice(i)) + '</span>';
  }

  campSparkSvg(n) {
    return `<svg class="cfg-cm__spark" width="${n}" height="${n}" viewBox="0 0 24 24"><path fill="currentColor" d="M12 1.5l2.2 7.1 7.3 2.4-7.3 2.4L12 20.5l-2.2-7.1L2.5 11l7.3-2.4z"/></svg>`;
  }

  /* Gift list: "Mom = her, necklace" lines. A person is kept only when at
     least one bundle offer matches one of their words (whole words of the
     offer's handle or name, plural "s" allowed). */
  campPeople() {
    const d = this.campaignData() || {};
    const offers = this.cfg.offers || [];
    const words = offers.map((o) => (String(o.handle || '') + ' ' + String(o.title || '')).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
    return String(d.people || '').split(/\r?\n/).map((line) => {
      const k = line.indexOf('=');
      if (k < 1) return null;
      const name = line.slice(0, k).trim();
      const kws = line.slice(k + 1).toLowerCase().split(',').map((x) => x.trim()).filter(Boolean);
      const idx = [];
      words.forEach((w, i) => { if (kws.some((kw) => w.some((t) => t === kw || t === kw + 's'))) idx.push(i); });
      return name && idx.length ? { name, idx } : null;
    }).filter(Boolean);
  }

  /* Offer indexes to show: the chosen person's pieces, otherwise all. */
  campOfferIdx() {
    const all = (this.cfg.offers || []).map((o, i) => i);
    const who = this.state.cmPerson;
    if (!who) return all;
    const p = this.campPeople().find((x) => x.name === who);
    return p ? p.idx : all;
  }

  campPeopleHtml(cls) {
    const who = this.state.cmPerson;
    return `<div class="cfg-cm-people${cls ? ' ' + cls : ''}" role="group" aria-label="Who is it for">${this.campPeople().map((p) => {
      const on = who === p.name;
      return `<button class="cfg-cm-person" data-action="cm-person" data-p="${esc(p.name)}" aria-pressed="${on}">${on ? '✓ ' : '+ '}${esc(p.name)}</button>`;
    }).join('')}</div>`;
  }

  campSaveHtml(o, c) {
    if (!c || !c.showSave || o.price == null || o.dealPrice == null || o.price <= o.dealPrice) return '';
    return `<span class="cfg-cm__save">Save ${esc(this.fmt(o.price - o.dealPrice))}</span>`;
  }

  /* ---------- render ---------- */
  render() {
    if (!this.hasAttribute('open')) return;
    const S = this.state;
    if (!this.root.querySelector('.cfg__dialog')) {
      this.root.innerHTML = `
        <div class="cfg__backdrop" data-action="backdrop"></div>
        <div class="cfg__dialog" role="dialog" aria-modal="true" aria-label="Customize your ${esc(this.cfg.product.title)}"></div>`;
    }
    const screens = {
      material: () => this.materialHtml(), charms: () => this.charmsHtml(), 'mf-charms': () => this.charmsHtml(), cord: () => this.cordHtml(), length: () => this.lengthHtml(), name: () => this.nameHtml(),
      style: () => this.styleHtml(), details: () => this.detailsHtml(this.newFlow ? { stonesOnly: true } : null), gift: () => this.giftHtml(), review: () => (this.newFlow ? this.reviewNewHtml() : this.reviewHtml()), deal: () => this.dealHtml(),
      'mf-name': () => this.mfNameHtml(), 'mf-style': () => this.styleHtml(), 'mf-finish': () => this.mfFinishHtml(),
      'mf-cord': () => this.cordHtml(), 'mf-length': () => this.lengthHtml(), 'mf-details': () => this.detailsHtml(this.splitGift() ? { stonesOnly: true } : null), 'mf-gift': () => this.giftHtml(),
    };
    const prevScreen = this._renderedScreen;
    this._renderedScreen = S.screen;
    const dialog = this.root.querySelector('.cfg__dialog');
    // Preserve scroll positions across same-screen re-renders (innerHTML rebuilds the containers)
    const prevBody = dialog.querySelector('.cfg__body');
    const keepScrollTop = prevScreen === S.screen && prevBody ? prevBody.scrollTop : null;
    const prevSheetOpen = !!dialog.querySelector('.cfg__sheet-backdrop');
    const keepRows = [];
    if (prevScreen === S.screen) {
      dialog.querySelectorAll('.cfg__stones-row').forEach((row) => keepRows.push(row.scrollLeft));
    }
    dialog.innerHTML = `
      ${S.confetti ? this.confettiHtml() : ''}
      ${this.headerHtml()}
      <div class="cfg__body">${(screens[S.screen] || screens.material)()}</div>
      <div class="cfg__footer" data-region="footer">${this.footerHtml()}</div>
      ${this.sheetsHtml()}
      ${S.toast ? `<div class="cfg__toast-wrap"><div class="cfg__toast"><span>${esc(S.toast.msg)}</span>${S.toast.undo ? '<button class="cfg__toast-undo" data-action="toast-undo">Undo</button>' : ''}</div></div>` : ''}`;
    const body = dialog.querySelector('.cfg__body');
    if (body) {
      if (prevScreen !== S.screen) body.scrollTop = 0;
      else if (keepScrollTop != null) body.scrollTop = keepScrollTop;
    }
    if (keepRows.length) {
      dialog.querySelectorAll('.cfg__stones-row').forEach((row, i) => {
        if (keepRows[i] != null) row.scrollLeft = keepRows[i];
      });
    }
    // A sheet that was already open must not replay its entry animation on
    // re-renders (tab switches, toggles inside the sheet).
    if (prevSheetOpen) {
      const bd = dialog.querySelector('.cfg__sheet-backdrop');
      const sh = dialog.querySelector('.cfg__sheet');
      if (bd) bd.classList.add('cfg__sheet-backdrop--settled');
      if (sh) sh.classList.add('cfg__sheet--settled');
    }
  }

  confettiHtml() {
    const cols = ['#C9A66E', '#8A6A2E', '#E8C98A', '#3F6A4C', '#17151A', '#B25B4C'];
    let out = '';
    for (let i = 0; i < 36; i++) {
      const round = Math.random() < 0.4;
      const left = (2 + Math.random() * 96).toFixed(1);
      out += `<span style="left:${left}%;width:${round ? 7 : 6}px;height:${round ? 7 : 11}px;background:${cols[i % cols.length]};border-radius:${round ? '50%' : '1px'};--cfx:${Math.round(-70 + Math.random() * 140)}px;--cfr:${Math.round(360 + Math.random() * 540)}deg;animation-duration:${(2 + Math.random() * 1.1).toFixed(2)}s;animation-delay:${(Math.random() * 0.7).toFixed(2)}s"></span>`;
    }
    return `<div class="cfg__confetti" aria-hidden="true">${out}</div>`;
  }

  headerHtml() {
    const S = this.state, mf = this.mfMode();
    if (S.screen === 'deal') {
      return `<div class="cfg__deal-banner">
        <span class="cfg__deal-banner-text"><span class="cfg__deal-banner-check">✓</span>&nbsp; Added to cart</span>
        <button class="cfg__deal-banner-close" data-action="close" aria-label="Close"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round"/></svg></button>
      </div>`;
    }
    const stepIdx = this.STEPS.findIndex((x) => x.id === S.screen);
    const title = S.screen === 'review' ? 'Review your design' : mf ? 'Customize your ' + (this.offerOf(S.mfOffer) || { title: 'piece' }).title.toLowerCase() : 'Customize your ' + (((this.cfg.product.title || '').trim().split(/\s+/).pop() || 'piece').toLowerCase());
    const showBackBtn = S.screen === 'review';
    let stepper = '';
    if (stepIdx >= 0) {
      stepper = `<div class="cfg__stepper" role="navigation" aria-label="Progress: step ${stepIdx + 1} of ${this.STEPS.length}">` + this.STEPS.map((s, i) => {
        const done = !!S.done[s.id] && S.screen !== s.id;
        const current = S.screen === s.id;
        const tappable = !!S.done[s.id] && !current;
        return `${i > 0 ? `<div class="cfg__stepper-line${S.done[this.STEPS[i - 1].id] ? ' cfg__stepper-line--done' : ''}"></div>` : ''}
          <button class="cfg__step-btn${current ? ' cfg__step-btn--current' : ''}${done ? ' cfg__step-btn--done' : ''}" ${tappable ? 'data-tappable' : ''} data-action="go-step" data-step="${s.id}" aria-disabled="${tappable ? 'false' : 'true'}" aria-label="Step ${i + 1} of ${this.STEPS.length}: ${s.label}${current ? ', current' : done ? ', completed' : ''}">
            <span class="cfg__step-dot">${done ? '<svg width="12" height="10" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : `<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="${s.d}" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`}</span>
            <span class="cfg__step-label">${s.label}</span>
          </button>`;
      }).join('') + '</div>';
    }
    let sub = '';
    if (S.screen === 'review') {
      sub = `<div class="cfg__dots"><div class="cfg__dots-track">${'<span class="cfg__dots-dot"></span><span class="cfg__dots-line"></span>'.repeat(this.STEPS.length - 1)}<span class="cfg__dots-dot"></span></div><span class="cfg__dots-label">All steps complete</span></div>`;
    }
    if (mf) {
      const list = this.mfSteps(); const mi = list.indexOf(S.screen);
      const names = { 'mf-charms': 'Charms', 'mf-name': 'Names', 'mf-style': 'Style', 'mf-finish': 'Finish', 'mf-cord': 'Cord color', 'mf-length': 'Length', 'mf-details': this.splitGift() ? 'Stones' : 'Gift extras', 'mf-gift': 'Gift' };
      const label = mi === -1 ? 'Style · quick change' : `Step ${mi + 1} of ${list.length} · ${names[S.screen]}`;
      const pct = mi === -1 ? 100 : Math.round(((mi + 1) / list.length) * 100);
      sub = `<div class="cfg__mini"><span class="cfg__mini-label">${label}</span><div class="cfg__mini-track"><div class="cfg__mini-fill" style="width:${pct}%"></div></div></div>`;
      const mfO = S.mf || {};
      if (S.screen !== 'mf-name' && S.screen !== 'mf-charms' && S.screen !== 'mf-style' && mfO.designSeq) {
        sub += `<div class="cfg__chip"><span class="cfg__chip-text">This piece: <b>${esc(this.namesDisplayOf(mfO.nameText || ''))}</b> · ${esc((this.styleOf(mfO.style || S.style) || {}).name || '')}</span><button class="cfg__chip-btn" data-action="ask-new-design">Change</button></div>`;
      }
    }
    if (S.editReturn && stepIdx >= 0) {
      sub += `<div class="cfg__edit-note"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5l2 2L4 10H2V8l6.5-6.5z" stroke="#8A6A2E" stroke-width="1.2" stroke-linejoin="round"/></svg><span>Editing from your review. Save to go back</span></div>`;
    }
    return `<div class="cfg__header">
      <div class="cfg__header-row">
        ${showBackBtn ? `<button class="cfg__icon-btn" data-action="header-back" aria-label="Back"><svg width="11" height="18" viewBox="0 0 11 18" fill="none"><path d="M9.5 1.5L2 9l7.5 7.5" stroke="#17151A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : ''}
        <div class="cfg__header-title">${esc(title)}</div>
        <button class="cfg__icon-btn" data-action="close" aria-label="Close configurator"><svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="#17151A" stroke-width="1.5" stroke-linecap="round"/></svg></button>
      </div>
      ${stepper}${sub}
    </div>${this.campRibbonHtml()}`;
  }

  helpBtn(key) { return `<button class="cfg__help-btn" data-action="help" data-help="${key}" aria-label="How it works">?</button>`; }

  /* Step photo with graceful fallback: src follows the file naming convention
     (cfg-finish-<handle>-<finish>.png etc.) and may not be uploaded yet —
     onerror swaps in the default step photo. */
  stepImgHtml(src, fallback, alt, ph) {
    if (!src || src === fallback) {
      return fallback ? `<img src="${esc(fallback)}" alt="${esc(alt)}">` : `<div class="cfg__hero-ph">${ph || 'Photo'}</div>`;
    }
    return `<img src="${esc(src)}" alt="${esc(alt)}"${fallback ? ` data-fb="${esc(fallback)}" onerror="this.onerror=null;this.src=this.dataset.fb"` : ' onerror="this.style.display=&#39;none&#39;"'}>`;
  }

  /* First not-yet-failed candidate wins; onerror marks it failed and the
     re-render moves to the next one. */
  cascadeImgHtml(cands, alt, ph) {
    const fail = this._lpFail || new Set();
    const src = (cands || []).filter((u) => u && !fail.has(u))[0];
    if (!src) return `<div class="cfg__hero-ph">${ph || 'Photo'}</div>`;
    return `<img src="${esc(src)}" alt="${esc(alt)}" data-src="${esc(src)}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)">`;
  }

  comboRecapHtml(cordId, finishId, cords) {
    const cord = this.cordOf(cordId, cords);
    const fin = this.finishOf(finishId);
    const finShort = fin.name.replace('14k ', '');
    return `<div class="cfg__combo-recap">
      <span class="cfg__combo-swatch" style="background:${cord.c}"></span>
      <span class="cfg__combo-plus">+</span>
      <span class="cfg__combo-swatch cfg__combo-swatch--round" style="background:${fin.grad}"></span>
      <span class="cfg__combo-text"><b>${cord.name}</b> cord with <b>${finShort}</b> charm</span>
    </div>`;
  }

  comboCands(combos, cordId, finishId) {
    const c = ((combos || {})[cordId] || {})[finishId];
    return c ? c.slice() : [];
  }

  zoomIcoHtml() {
    return `<span class="cfg__zoom-ico" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.4" stroke="currentColor" stroke-width="1.5"/><path d="M9.4 9.4L13 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M4.2 6h3.6M6 4.2v3.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg></span>`;
  }

  styleThumbHtml(s, finishId) {
    const img = ((this.cfg.images || {}).styles || {})[s.id];
    if (img) return `<span class="cfg__style-thumb"><img src="${esc(img)}" alt=""></span>`;
    const urls = this.styleExUrls(s);
    if (urls && urls[0]) {
      return `<span class="cfg__style-thumb cfg__style-thumb--img cfg__style-thumb--fin" style="--cfg-mask:url('${esc(urls[0])}')"><img class="cfg__mask-probe" src="${esc(urls[0])}" alt="" data-sid="${s.id}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{styleExFailed(){}}).styleExFailed(this.dataset.sid)"></span>`;
    }
    const pair = s.ex[0];
    let inner = '';
    if (s.mode === 'flow') inner = `<span class="cfg-serif cfg__ex-flow-a">${pair[0]}</span><span class="cfg-serif cfg__ex-flow-b">${pair[1] || ''}</span>`;
    if (s.mode === 'stack') inner = `<span class="cfg__ex-stack"><span>${pair[0]}</span><span class="cfg__ex-stack-bar"></span><span>${pair[1] || ''}</span></span>`;
    if (s.mode === 'mirror') inner = `<span class="cfg__ex-mirror"><span class="cfg-serif">${pair[0]}</span><span class="cfg-serif">${pair[0]}</span></span><span class="cfg__ex-heart">♥</span>`;
    return `<span class="cfg__style-thumb cfg__style-thumb--gen">${inner}</span>`;
  }

  finishCardsHtml(sel) {
    return `<div class="cfg__grid-2">` + this.finishList(this.ctxFinishIds()).map((f) => `
      <button class="cfg__finish-card" data-action="select-finish" data-id="${f.id}" data-selected="${sel === f.id}" aria-label="${f.name}, ${f.benefit}${sel === f.id ? ', selected' : ''}">
        <span class="cfg__finish-swatch" style="background:${f.grad}"></span>
        <span><span class="cfg__finish-name"${sel === f.id ? ' style="color:#8A6A2E"' : ''}>${f.name}</span><span class="cfg__finish-benefit">${f.benefit}</span></span>
        ${f.badge && !this.mfMode() ? '<span class="cfg__badge">Most gifted</span>' : ''}
      </button>`).join('') + '</div>';
  }

  materialHtml() {
    const S = this.state;
    if (this.cfg.hasCord && S.cord) {
      const cands = this.comboCands(this.cfg.cordCombos, S.cord, S.finish);
      const fin = (this.cfg.images.finish || {})[S.finish];
      if (fin) cands.push(fin);
      if (this.cfg.images.hero) cands.push(this.cfg.images.hero);
      return `<div class="cfg__screen">
      <div class="cfg__hero">${this.cascadeImgHtml(cands, this.cfg.product.title, 'Product photo')}</div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">Choose the finish for your jewelry</span>${this.helpBtn('material')}</div>
      ${this.finishCardsHtml(S.finish)}
      ${this.comboRecapHtml(S.cord, S.finish, this.cfg.cords)}
      <div class="cfg__hint">Premium stainless steel. Won't fade or tarnish.</div>
    </div>`;
    }
    const finishImg = (this.cfg.images.finish || {})[this.state.finish];
    return `<div class="cfg__screen">
      <div class="cfg__hero">${this.stepImgHtml(finishImg, this.cfg.images.hero, this.cfg.product.title, 'Necklace photo')}</div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">Choose the finish for your jewelry</span>${this.helpBtn('material')}</div>
      ${this.finishCardsHtml(this.state.finish)}
      <div class="cfg__hint">Premium stainless steel. Won't fade or tarnish.</div>
    </div>`;
  }

  /* Generic 404 fallback for convention-named files: mark the URL as failed
     and re-render so the markup picks the next candidate. */
  srcFailed(url) {
    this._lpFail = this._lpFail || new Set();
    this._lpFail.add(url);
    this.render();
  }

  lengthPhotoFailed(url) { this.srcFailed(url); }

  /* Style example medallions: one failed cfg-style-*.svg falls the whole
     style back to the generated letter examples. */
  styleExFailed(styleId) {
    this._sxFail = this._sxFail || new Set();
    this._sxFail.add(styleId);
    this.render();
  }

  styleExUrls(s) {
    const urls = ((this.cfg.images || {}).styleEx || {})[s.id];
    if (urls && urls.length && !(this._sxFail && this._sxFail.has(s.id))) return urls;
    return null;
  }

  /* Length step photo for the current finish. Cascade:
     cfg-length-<handle>-<finish>.png -> cfg-length-<handle>.png -> null (drawn arcs). */
  lengthPhotoFor(o) {
    const map = (this.mfMode()
      ? (this.offerOf(this.state.mfOffer) || {}).lengthPhotos
      : this.cfg.images.lengthPhotos) || {};
    const fail = this._lpFail || new Set();
    const candidates = [map[o.finish || 'gold'], map.default];
    for (const u of candidates) {
      if (u && !fail.has(u)) return u;
    }
    return null;
  }

  lengthHtml() {
    const o = this.cur();
    const fin = this.finishOf(o.finish);
    const LS = this.lengthsFor();
    const selDef = this.defaultLength(LS);
    // Real model photo with all 4 chains: tappable length markers pinned on
    // each chain. Falls back to the drawn arcs when no photo is uploaded.
    const lengthPhoto = this.lengthPhotoFor(o);
    if (lengthPhoto) {
      const marks = LS.map((L, li) => {
        const sel = (o.length || selDef) === L;
        return `<button class="cfg__length-mark" style="top:${LS.length === 4 ? LENGTH_MARKS[L] : 44.5 + 25 * (li / Math.max(1, LS.length - 1))}%" data-on="${sel}" data-action="select-length" data-n="${L}" aria-label="${L} inches${sel ? ', selected' : ''}">${L}″</button>`;
      }).join('');
      const opts2 = LS.map((L) => {
        const sel = (o.length || selDef) === L;
        return `<button class="cfg__length-card" data-action="select-length" data-n="${L}" data-selected="${sel}" aria-label="${L} inches${sel ? ', selected' : ''}">
          <span class="cfg__length-num cfg-serif"${sel ? ' style="color:#8A6A2E"' : ''}>${L}</span><span class="cfg__length-unit">inches</span>
          ${L === 18 ? '<span class="cfg__badge">Most chosen</span>' : ''}
        </button>`;
      }).join('');
      return `<div class="cfg__screen">
        <div class="cfg__length-shot">
          <div class="cfg__length-frame">
            <img src="${esc(lengthPhoto)}" alt="Chain lengths on a model" data-src="${esc(lengthPhoto)}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{lengthPhotoFailed(){}}).lengthPhotoFailed(this.dataset.src)">
            ${marks}
          </div>
        </div>
        <div class="cfg__row-title"><span class="cfg__row-title-text">Choose your chain length</span>${this.helpBtn('length')}</div>
        <div class="cfg__grid-2">${opts2}</div>
      </div>`;
    }
    const geo = { 16: { w: 200, h: 110, top: 70 }, 18: { w: 240, h: 135, top: 76 }, 20: { w: 278, h: 160, top: 82 }, 22: { w: 314, h: 185, top: 88 } };
    const arcs = LS.map((L) => {
      const g = geo[L], sel = (o.length || selDef) === L;
      return `<div class="cfg__arc" style="width:${g.w}px;height:${g.h}px;top:${g.top}px;border:${sel ? '2px solid ' + fin.chain : '1.5px solid rgba(255,255,255,.8)'};opacity:${sel ? 1 : 0.55}"></div>
        <span class="cfg__arc-label" style="left:calc(50% + ${g.w / 2 + 6}px);top:${g.top + g.h / 2 - 10}px;font-weight:${sel ? 700 : 500};color:${sel ? '#FFFFFF' : '#5E656C'};background:${sel ? 'rgba(23,21,26,.78)' : 'rgba(255,255,255,.6)'}">${L}″</span>
        ${sel ? `<span class="cfg__arc-pendant" style="top:${g.top + g.h - 11}px;background:${fin.grad}"></span>` : ''}`;
    }).join('');
    const opts = LS.map((L) => {
      const sel = (o.length || selDef) === L;
      return `<button class="cfg__length-card" data-action="select-length" data-n="${L}" data-selected="${sel}" aria-label="${L} inches${sel ? ', selected' : ''}">
        <span class="cfg__length-num cfg-serif"${sel ? ' style="color:#8A6A2E"' : ''}>${L}</span><span class="cfg__length-unit">inches</span>
        ${L === 18 ? '<span class="cfg__badge">Most chosen</span>' : ''}
      </button>`;
    }).join('');
    const img = this.cfg.images.neck;
    return `<div class="cfg__screen">
      <div class="cfg__hero cfg__hero--neck">${img ? `<img src="${esc(img)}" alt="Chain length on a model">` : '<div class="cfg__hero-ph">Model neckline photo</div>'}<div class="cfg__arcs">${arcs}</div></div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">Choose your chain length</span>${this.helpBtn('length')}</div>
      <div class="cfg__grid-2">${opts}</div>
    </div>`;
  }

  /* Charm bracelet: how many charms (1 to 4). The photo follows the count
     (variant images of the "charm" product), the price of each option is the
     charm product's variant price. */
  charmsHtml() {
    const o = this.cur();
    const n = o.charmCount || 1;
    const max = this.ctxMaxCharms();
    const ch = this.cfg.charms || {};
    const offer = this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}) : null;
    const img = (ch[String(n)] || {}).image || (offer ? offer.image : this.cfg.images.hero);
    const cards = [];
    for (let k = 1; k <= max; k++) {
      const v = ch[String(k)] || {};
      const extra = v.price || 0;
      const sel = n === k;
      cards.push(`<button class="cfg__length-card cfg__charm-card" data-action="select-charms" data-n="${k}" data-selected="${sel}" aria-label="${k} ${k > 1 ? 'charms' : 'charm'}${extra ? ', plus ' + this.fmt(extra) : ', included'}${sel ? ', selected' : ''}">
        <span class="cfg__length-num cfg-serif"${sel ? ' style="color:#8A6A2E"' : ''}>${k}</span><span class="cfg__length-unit">${k > 1 ? 'charms' : 'charm'}</span>
        <span class="cfg__charm-price">${extra ? '+' + this.fmt(extra) : 'Included'}</span>
        ${k === 2 ? '<span class="cfg__badge">Most chosen</span>' : ''}
      </button>`);
    }
    return `<div class="cfg__screen">
      <div class="cfg__hero">${img ? `<img src="${esc(img)}" alt="Bracelet with ${n} ${n > 1 ? 'charms' : 'charm'}">` : '<div class="cfg__hero-ph">Bracelet photo</div>'}</div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">How many charms?</span></div>
      <div class="cfg__grid-2">${cards.join('')}</div>
      <div class="cfg__hint">Each charm gets its own hand-drawn monogram.</div>
    </div>`;
  }

  /* One input per charm. key: cname (main), mf-cname (bundle piece),
     ie-cname (inline edit); countAction adds the count chips (review/edit). */
  charmFieldsHtml(o, key, countAction, maxCharms) {
    const n = Math.max(1, o.charmCount || 1);
    const ex = ['Anna', 'Luca, Sofia', 'B, L, P, T', 'Grace'];
    let out = '';
    if (countAction) {
      const max = Math.max(1, maxCharms || this.ctxMaxCharms());
      const chips = [];
      for (let k = 1; k <= max; k++) chips.push(`<button class="cfg__rev-chip cfg__rev-chip--len" data-on="${n === k}" data-action="${countAction}" data-n="${k}" aria-label="${k} ${k > 1 ? 'charms' : 'charm'}${n === k ? ', selected' : ''}">${k}</button>`);
      out += `<div class="cfg__rev-field"><span class="cfg__rev-label">CHARMS</span><div class="cfg__rev-row">${chips.join('')}</div></div>`;
    }
    for (let k = 0; k < n; k++) {
      const v = (o.names || [])[k] || '';
      const bad = v.trim() && !this.charmNameOk(v);
      out += `<div class="cfg__rev-field cfg__charm-field"><span class="cfg__rev-label">CHARM ${k + 1}</span>
        <input class="cfg__input${countAction ? ' cfg__input--small' : ''}${bad ? ' cfg__input--invalid' : ''}" data-input="${key}" data-k="${k}" value="${esc(v)}" placeholder="e.g. ${esc(ex[k] || 'Name')}" autocomplete="off" aria-label="Name for charm ${k + 1}"></div>`;
    }
    const region = key === 'ie-cname' ? 'ie-msgs' : 'name-msgs';
    return out + `<div data-region="${region}" style="display:flex;flex-direction:column;gap:6px">${this.charmMsgsHtml(o)}</div>`;
  }

  charmMsgsHtml(o) {
    const n = Math.max(1, o.charmCount || 1);
    for (let k = 0; k < n; k++) {
      const t = String(((o.names || [])[k]) || '').trim();
      if (!t) continue;
      const bad = this.charBad(t);
      if (bad) return `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>Charm ${k + 1}: letters, numbers, spaces, hyphens and apostrophes only. Remove "${esc(bad)}" to continue.</span></div>`;
      if (this.tooManyNames(t)) return `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>Charm ${k + 1}: more than 6 names. Keep it to 6, or use initials.</span></div>`;
    }
    return '';
  }

  nameMsgsHtml() {
    const mf = this.mfMode();
    if (this.ctxMulti()) return this.charmMsgsHtml(this.cur());
    const text = mf ? (this.state.mf.nameText || '') : this.state.nameText;
    const bad = this.charBad(text.trim());
    const tooMany = this.tooManyNames(text);
    let out = '';
    if (bad || tooMany) {
      const msg = bad
        ? `Letters, numbers, spaces, hyphens and apostrophes only. Remove "${esc(bad)}" to continue.`
        : "That's more than 6 names. Keep it to 6, or use initials: they all fit.";
      out += `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>${msg}</span></div>`;
    } else if (mf && text.trim() && !this.state.mf.designSeq) {
      out += `<div class="cfg__msg cfg__msg--info"><span class="cfg__msg-icon">ⓘ</span><span>A new name gets its own hand-drawn draft, included at no extra cost.</span></div>`;
    }
    return out;
  }

  nameHtml() {
    const S = this.state;
    if (this.cfg.multiCharm) return this.multiNameHtml(S, 'cname');
    const monoImgs = this.cfg.images.mono || {};
    const cards = MONO.map((r) => {
      const sel = S.pattern === r.id;
      const img = monoImgs[r.id];
      return `<button class="cfg__mono-card" data-action="select-pattern" data-id="${r.id}" data-selected="${sel}" aria-label="${r.who}, ${r.label}, example ${r.ex}${sel ? ', selected' : ''}">
        ${img ? `<img class="cfg__mono-img" src="${esc(img)}" alt="">` : `<span class="cfg__mono-img-ph cfg-serif" style="color:#C9A66E">${r.ex.split(',')[0].trim().slice(0, 1)}</span>`}
        <span class="cfg__mono-copy"><span class="cfg__mono-who">${r.who}</span><span class="cfg__mono-sub">${r.label} · ${r.ex}</span></span>
        <span class="cfg__radio${sel ? ' cfg__radio--on' : ''}">${sel ? '<span class="cfg__radio-dot"></span>' : ''}</span>
      </button>`;
    }).join('');
    const prev = this.pieceDesigns().filter((d) => d.designSeq !== S.designSeq);
    const prevHtml = prev.length ? `<div class="cfg__prev"><span class="cfg__prev-label">NAMES YOU'VE ALREADY DESIGNED</span><div class="cfg__prev-chips">${prev.map((d, i) => {
      const df = this.finishOf(d.finish);
      return `<button class="cfg__prev-chip" data-action="use-name" data-i="${i}" aria-label="Use ${esc(this.namesDisplayOf(d.nameText))}"><span class="cfg__prev-chip-disc" style="background:${df.grad}"><span class="cfg__prev-chip-ini cfg-serif" style="color:${df.letter}">${esc(this.initialsOf(d.nameText))}</span></span><span class="cfg__prev-chip-name">${esc(this.namesDisplayOf(d.nameText))}</span></button>`;
    }).join('')}</div></div>` : '';
    const ph = S.pattern ? MONO.find((r) => r.id === S.pattern).ex : 'Names, initials, or a word';
    const invalid = S.nameText.trim() && !this.nameValid();
    return `<div class="cfg__screen" style="gap:14px">
      <div style="display:flex;flex-direction:column;gap:8px;padding-top:4px">
        <span class="cfg__row-title-text">What will this monogram hold?</span>
        <div class="cfg__grid-2" style="gap:8px">${cards}</div>
      </div>
      ${prevHtml}
      <div style="display:flex;flex-direction:column;gap:8px">
        <div class="cfg__row-title"><span class="cfg__row-title-text">Enter text for your monogram</span>${this.helpBtn('name')}</div>
        <div class="cfg__input-wrap"><input class="cfg__input${invalid ? ' cfg__input--invalid' : ''}" data-input="name" value="${esc(S.nameText)}" placeholder="${esc(ph)}" autocomplete="off"></div>
        <div class="cfg__hint cfg__hint--left">The designer will adapt your text to fit the monogram</div>
        <div data-region="name-msgs" style="display:flex;flex-direction:column;gap:6px">${this.nameMsgsHtml()}</div>
      </div>
    </div>`;
  }

  multiNameHtml(o, key) {
    const n = Math.max(1, o.charmCount || 1);
    return `<div class="cfg__screen" style="gap:14px">
      <div style="display:flex;flex-direction:column;gap:4px;padding-top:4px">
        <div class="cfg__row-title"><span class="cfg__row-title-text">${n > 1 ? 'A name for each charm' : 'A name for your charm'}</span>${this.helpBtn('name')}</div>
        <span class="cfg__hint cfg__hint--left">Each charm holds one monogram: a name, two names or family initials.</span>
      </div>
      <div style="display:flex;flex-direction:column;gap:10px">${this.charmFieldsHtml(o, key)}</div>
      <div class="cfg__hint cfg__hint--left">The designer will adapt your text to fit each monogram</div>
    </div>`;
  }

  mfNameHtml() {
    const S = this.state, mfO = S.mf || {};
    if ((this.offerOf(S.mfOffer) || {}).multiCharm) return this.multiNameHtml(mfO, 'mf-cname');
    const designs = this.pieceDesigns();
    const rows = designs.map((d, i) => {
      const sel = mfO.designSeq === d.designSeq;
      const df = this.finishOf(d.finish);
      const stN = (this.styleOf(d.style) || {}).name || '';
      return `<button class="cfg__mono-card" style="flex-direction:row;align-items:center;gap:12px;min-height:56px;padding:8px 12px" data-action="use-design" data-i="${i}" data-selected="${sel}" aria-label="Use ${esc(this.namesDisplayOf(d.nameText))}${sel ? ', selected' : ''}">
        <span class="cfg__prev-chip-disc" style="width:38px;height:38px;background:${df.grad};flex-shrink:0"><span class="cfg-serif" style="font-size:10px;color:${df.letter}">${esc(this.initialsOf(d.nameText))}</span></span>
        <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:1px"><span style="font-size:15px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(this.namesDisplayOf(d.nameText))}</span><span style="font-size:12.5px;color:#565356">${esc(stN)}</span></span>
        <span class="cfg__radio${sel ? ' cfg__radio--on' : ''}" style="position:static">${sel ? '<span class="cfg__radio-dot"></span>' : ''}</span>
      </button>`;
    }).join('');
    const invalid = (mfO.nameText || '').trim() && !this.mfNameValid();
    return `<div class="cfg__screen" style="gap:14px;padding-top:8px">
      <div style="display:flex;flex-direction:column;gap:2px">
        <span class="cfg__row-title-text">Enter text for your next monogram</span>
        <span class="cfg__hint cfg__hint--left">One tap fills it in, or type a new one below.</span>
      </div>
      ${designs.length ? `<div style="display:flex;flex-direction:column;gap:6px">${rows}</div>
      <div style="display:flex;align-items:center;gap:10px"><span style="flex:1;height:1px;background:#EFEAE1"></span><span style="font-size:13px;color:#6C6A6B">or a new name</span><span style="flex:1;height:1px;background:#EFEAE1"></span></div>` : ''}
      <div class="cfg__input-wrap"><input class="cfg__input${invalid ? ' cfg__input--invalid' : ''}" data-input="mf-name" value="${esc(mfO.nameText || '')}" placeholder="Type a name" autocomplete="off"></div>
      <div data-region="name-msgs" style="display:flex;flex-direction:column;gap:6px;margin-top:-6px">${this.nameMsgsHtml()}</div>
    </div>`;
  }

  styleExHtml(s, big) {
    const urls = this.styleExUrls(s);
    if (urls) {
      return urls.slice(0, 4).map((u) => `<span class="${big ? 'cfg__zoom-ex' : 'cfg__style-ex'} cfg__style-ex--img cfg__style-ex--fin" ${big ? '' : `role="button" aria-label="Zoom examples" data-action="zoom-style" data-id="${s.id}"`} style="--cfg-mask:url('${esc(u)}')"><img class="cfg__mask-probe" src="${esc(u)}" alt="" data-sid="${s.id}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{styleExFailed(){}}).styleExFailed(this.dataset.sid)"></span>`).join('');
    }
    return s.ex.map((pair) => {
      let inner = '';
      if (s.mode === 'flow') inner = `<span class="cfg-serif cfg__ex-flow-a">${pair[0]}</span><span class="cfg-serif cfg__ex-flow-b">${pair[1] || ''}</span>`;
      if (s.mode === 'stack') inner = `<span class="cfg__ex-stack"><span>${pair[0]}</span><span class="cfg__ex-stack-bar"></span><span>${pair[1] || ''}</span></span>`;
      if (s.mode === 'mirror') inner = `<span class="cfg__ex-mirror"><span class="cfg-serif">${pair[0]}</span><span class="cfg-serif">${pair[0]}</span></span><span class="cfg__ex-heart">♥</span>`;
      return `<span class="${big ? 'cfg__zoom-ex' : 'cfg__style-ex'}" ${big ? '' : `role="button" aria-label="Zoom examples" data-action="zoom-style" data-id="${s.id}"`}>${inner}</span>`;
    }).join('');
  }

  styleHtml() {
    const o = this.cur();
    const cards = STYLES.map((s) => {
      const sel = o.style === s.id;
      const useSvg = !!this.styleExUrls(s);
      return `<button class="cfg__style-card" data-action="select-style" data-id="${s.id}" data-selected="${sel}" aria-label="${s.name}, ${s.desc}${sel ? ', selected' : ''}">
        <div class="cfg__style-exs${useSvg ? ' cfg__style-exs--img' : ''}">${this.styleExHtml(s)}</div>
        <div class="cfg__style-meta">
          <span class="cfg__radio${sel ? ' cfg__radio--on' : ''}">${sel ? '<span class="cfg__radio-dot"></span>' : ''}</span>
          <span style="flex:1"><span class="cfg__style-name">${s.name}${s.badge ? ` <span class="cfg__pill-badge">${s.badge}</span>` : ''}</span><span class="cfg__style-desc">${s.desc}</span></span>
        </div>
      </button>`;
    }).join('');
    return `<div class="cfg__screen" style="gap:10px">
      <div class="cfg__row-title" style="padding-top:4px"><span class="cfg__row-title-text">Choose a style</span>${this.helpBtn('style')}</div>
      ${cards}
    </div>`;
  }

  mfFinishHtml() {
    const S = this.state, mfO = S.mf || {};
    const offer = this.offerOf(S.mfOffer) || {};
    if (offer.hasCord && mfO.cord) {
      const cands = this.comboCands(offer.cordCombos, mfO.cord, mfO.finish);
      const fin = (offer.finishImages || {})[mfO.finish];
      if (fin) cands.push(fin);
      if (offer.image) cands.push(offer.image);
      return `<div class="cfg__screen">
      <div class="cfg__hero">${this.cascadeImgHtml(cands, offer.title || '', 'Product photo')}</div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">Choose the finish for your jewelry</span>${this.helpBtn('material')}</div>
      ${this.finishCardsHtml(mfO.finish)}
      ${this.comboRecapHtml(mfO.cord, mfO.finish, offer.cords)}
    </div>`;
    }
    const finishImg = (offer.finishImages || {})[mfO.finish];
    return `<div class="cfg__screen">
      <div class="cfg__hero">${this.stepImgHtml(finishImg, offer.image, offer.title || '', 'Product photo')}</div>
      <div class="cfg__row-title"><span class="cfg__row-title-text">Choose the finish for your jewelry</span>${this.helpBtn('material')}</div>
      ${this.finishCardsHtml(mfO.finish)}
    </div>`;
  }

  cordHtml() {
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const cord = this.cordOf(o.cord);
    const offer = mf ? (this.offerOf(S.mfOffer) || {}) : null;
    const cordImgs = mf ? offer.cordImages : this.cfg.images.cordImages;
    const fallback = mf ? offer.image : this.cfg.images.hero;
    const alt = mf ? (offer.title || '') : this.cfg.product.title;
    const cards = this.cordsFor().map((cd) => {
      const sel = o.cord === cd.id;
      return `<button class="cfg__finish-card" data-action="select-cord" data-id="${cd.id}" data-selected="${sel}" aria-label="${cd.name} cord${sel ? ', selected' : ''}" style="${sel ? 'border-color:#17151A' : ''}">
        <span class="cfg__finish-swatch cfg__finish-swatch--round" style="background:${cd.c}"></span>
        <span class="cfg__finish-name">${cd.name}</span>
      </button>`;
    }).join('');
    const combos = mf ? offer.cordCombos : this.cfg.cordCombos;
    const cands = this.comboCands(combos, o.cord, 'black');
    if ((cordImgs || {})[o.cord]) cands.push(cordImgs[o.cord]);
    if (fallback) cands.push(fallback);
    const names = mf ? (S.mf.nameText || S.nameText) : S.nameText;
    return `<div class="cfg__screen">
      <div class="cfg__hero">${this.cascadeImgHtml(cands, alt, 'Product photo')}</div>
      ${names && names.trim() ? `<div class="cfg__hint" style="font-size:13px"><b>${esc(this.namesDisplayOf(names))}</b> · <b>${cord.name}</b> cord</div>` : ''}
      <div class="cfg__row-title"><span class="cfg__row-title-text">Pick the cord color</span></div>
      <div class="cfg__grid-2">${cards}</div>
    </div>`;
  }

  stoneCardsHtml(o, opts) {
    const op = opts || {};
    const mini = !!op.mini;
    const price = op.stonePrice != null ? op.stonePrice : this.cfg.stonePrice;
    const pre = op.prefix || 'stone';
    const src0 = op.stoneFiles ? op : (this.mfMode() ? (this.offerOf(this.state.mfOffer) || {}) : this.cfg);
    const files = src0.stoneFiles || {};
    const imgs = src0.stoneImages || {};
    const sl = op.stoneFiles ? (op.stones || null) : undefined;
    return this.stonesList(sl).map((s, i) => {
      const meta = this.stoneMetaAt(i, sl);
      const q = (o.stones || {})[i] || 0;
      const img = this.stoneSrc(meta, files, imgs);
      const visual = img
        ? `<span class="cfg__stone-visual"><img src="${esc(img)}" alt="${esc(meta.stone)}" data-src="${esc(img)}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)"></span>`
        : `<span class="cfg__stone-gem" style="background:${meta.c}"></span>`;
      const control = q > 0
        ? `<div class="cfg__qty">
            <button class="cfg__qty-minus" data-action="${pre}-dec" data-i="${i}" aria-label="Remove ${esc(meta.stone)} birthstone">−</button>
            <span class="cfg__qty-num">${q}</span>
            <button class="cfg__qty-plus" data-action="${pre}-inc" data-i="${i}" aria-label="Add ${esc(meta.stone)} birthstone">+</button>
          </div>
          <span class="cfg__stone-price-badge">+${this.fmt(q * price)}</span>`
        : `<button class="cfg__stone-add" data-action="${pre}-inc" data-i="${i}" aria-label="Add ${esc(meta.stone)} birthstone">Add · ${this.fmt(price)}</button>`;
      return `<div class="cfg__stone-card${mini ? ' cfg__stone-card--mini' : ''}" data-on="${q > 0}">
        ${visual}
        <span class="cfg__stone-month">${esc(meta.month || '')}</span>
        ${mini ? '' : `<span class="cfg__stone-stone">${esc((meta.stone || '').toUpperCase())}</span>
        <span class="cfg__stone-meaning">${esc(meta.meaning || '')}</span>`}
        ${control}
      </div>`;
    }).join('');
  }

  detailsHtml(opts) {
    const stonesOnly = !!(opts && opts.stonesOnly);
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const fin = this.finishOf(o.finish || S.finish);
    const total = this.stoneCount(o);
    const offer = mf ? this.offerOf(S.mfOffer) : null;
    const stonePrice = mf && offer ? offer.stonePrice : this.cfg.stonePrice;
    const maxS = this.maxStonesFor();
    const hideS = this.hideStonesFor();
    const names = mf ? (S.mf.nameText || S.nameText) : S.nameText;
    // Live preview per product family: necklace = arc with hanging pendant;
    // bracelet/cord = taut horizontal line, charm threaded in the middle;
    // keychain = ring + short vertical chain with the charm at the bottom.
    // Selected stones render as their PNG beads (fallback: colored dot).
    const ctxTitle = ((mf ? (offer || {}).title : this.cfg.product.title) || '').toLowerCase();
    const ctxHandle = ((mf ? (offer || {}).handle : this.cfg.product.handle) || '').toLowerCase();
    const isNeck = mf ? !!(offer && offer.hasLength && !offer.hasCord) : (this.cfg.hasLength && !this.cfg.hasCord);
    // charms (bag charm, pet charm, family initials charm) hang like a keychain
    const isBrac = ctxTitle.indexOf('bracelet') !== -1 || ctxHandle.indexOf('bracelet') !== -1;
    const isKeych = !isNeck && !isBrac && (ctxTitle.indexOf('keychain') !== -1 || ctxHandle.indexOf('keychain') !== -1 || ctxTitle.indexOf('charm') !== -1 || ctxHandle.indexOf('charm') !== -1);
    const isLine = !isNeck && !isKeych && ((mf ? !!(offer || {}).hasCord : this.cfg.hasCord) || ctxTitle.indexOf('bracelet') !== -1 || ctxHandle.indexOf('bracelet') !== -1);
    const beadFiles = ((mf ? (offer || {}).stoneFiles : this.cfg.stoneFiles) || {});
    const beadFail = this._lpFail || new Set();
    const flat = [];
    Object.keys(o.stones || {}).sort((a, b) => a - b).forEach((k) => { for (let j = 0; j < o.stones[k]; j++) flat.push(+k); });
    const beadAt = (mi, x, y) => {
      const meta = this.stoneMetaAt(mi);
      const src = (beadFiles[meta.month] && !beadFail.has(beadFiles[meta.month])) ? beadFiles[meta.month] : meta.image;
      if (src) return `<img class="cfg__chain-bead" src="${esc(src)}" alt="${esc(meta.stone)}" style="left:${(x - 9).toFixed(1)}px;top:${(y - 17).toFixed(1)}px">`;
      return `<span class="cfg__chain-stone" style="left:${(x - 6.5).toFixed(1)}px;top:${(y - 6.5).toFixed(1)}px;background:${meta.c}"></span>`;
    };
    const lineColor = (mf ? !!(offer || {}).hasCord : this.cfg.hasCord) ? this.cordOf(o.cord, mf ? (offer || {}).cords : this.cfg.cords).c : fin.chain;
    const strand = (mf ? !!(offer || {}).hasCord : this.cfg.hasCord) ? 'cord' : 'chain'; // copy for cord bracelets
    const discHtml = (extra, style) => `<div class="cfg__chain-disc${extra || ''}" style="background:${fin.grad};${style || ''}"><span class="cfg-serif" style="color:${fin.letter}">${esc(this.initialsOf(names))}</span></div>`;
    let stageInner = '';
    const shown = flat.slice(0, Math.min(6, maxS));
    if (isKeych) {
      const beadYs = [44, 61, 78];
      stageInner = `<div class="cfg__chain-ring" style="border-color:${fin.chain}"></div>
        <div class="cfg__chain-vline" style="background:${fin.chain}"></div>
        ${shown.slice(0, 3).map((mi, i) => beadAt(mi, 140, beadYs[i])).join('')}
        ${discHtml(' cfg__chain-disc--drop', 'top:88px;')}`;
    } else if (isLine && this.ctxMulti()) {
      const L = this.charmLine(o, fin);
      stageInner = `<div class="cfg__chain-hline" style="background:${lineColor}"></div>
        ${shown.map((mi, i) => beadAt(mi, L.slots[i], 66)).join('')}
        ${L.discs}`;
    } else if (isLine) {
      const offs = [-40, 40, -64, 64, -88, 88];
      stageInner = `<div class="cfg__chain-hline" style="background:${lineColor}"></div>
        ${shown.map((mi, i) => beadAt(mi, 140 + offs[i], 66)).join('')}
        ${discHtml('', 'top:49px;')}`;
    } else {
      const slotOrder = [[1, 1], [1, -1], [2, 1], [2, -1], [3, 1], [3, -1]];
      stageInner = `<div class="cfg__chain-arc" style="border-color:${fin.chain}"></div>
        ${shown.map((mi, i) => {
          const n = slotOrder[i][0], side = slotOrder[i][1];
          const phi = 0.24 + 0.21 * (n - 1);
          const x = 140 + side * 120 * Math.sin(phi), y = -4 + 112 * Math.cos(phi);
          return isNeck ? beadAt(mi, x, y) : (() => { const meta = this.stoneMetaAt(mi); return `<span class="cfg__chain-stone" style="left:${(x - 6.5).toFixed(1)}px;top:${(y - 6.5).toFixed(1)}px;background:${meta.c}"></span>`; })();
        }).join('')}
        ${discHtml(isNeck ? ' cfg__chain-disc--drop' : '', '')}`;
    }
    const mainHasExtras = this.stoneCount(S) > 0 || S.scratch || S.gift;
    const sameDesign = mf && ((S.mf.designSeq === S.designSeq) || ((S.mf.nameText || '').trim().toUpperCase() === S.nameText.trim().toUpperCase()));
    const showCopyCard = mf && S.extrasChoice === null && mainHasExtras && sameDesign;
    const exParts = [];
    const nst = this.stoneCount(S);
    if (nst) exParts.push(nst + ' birthstone' + (nst > 1 ? 's' : ''));
    if (S.scratch) exParts.push('scratch card');
    if (S.gift) exParts.push('gift box');
    const scr = !!o.scratch, gf = !!o.gift;
    return `<div class="cfg__screen" style="gap:14px">
      <div class="cfg__row-title" style="padding-top:4px"><span class="cfg__row-title-text">${stonesOnly ? 'Add birthstones' : 'Gift extras'} <span class="cfg__optional">(optional)</span></span>${this.helpBtn('details')}</div>
      ${showCopyCard ? `<div class="cfg__copy-extras">
        <span class="cfg__copy-extras-title">⟳ Same extras as your ${this.pieceNoun()}?</span>
        <span class="cfg__copy-extras-sub">${esc(exParts.join(' · ') || 'No extras on your ' + this.pieceNoun())}</span>
        <div class="cfg__copy-extras-row">
          <button class="cfg__btn-solid" style="flex:1;height:40px" data-action="copy-extras">Yes, copy them</button>
          <button class="cfg__btn-ghost" style="flex:1;height:40px" data-action="choose-extras">Choose</button>
        </div>
      </div>` : ''}
      ${mf && S.extrasChoice === 'copied' ? '<div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><span>Copied from your ' + this.pieceNoun() + '. Adjust anything below.</span></div>' : ''}
      ${hideS ? '' : `<div style="display:flex;flex-direction:column;gap:8px">
        <div class="cfg__section-label-row"><span class="cfg__section-label">BIRTHSTONES</span><span class="cfg__section-counter">${total} of ${maxS} added</span></div>
        <div class="cfg__stones-preview">
          ${(() => {
            const im = this.cfg.images;
            const fail = this._lpFail || new Set();
            const ctxPhoto = mf ? (offer || {}).stonesPhoto : im.stonesPhoto;
            const ctxShared = mf ? (offer || {}).stonesPhotoShared : im.stonesPhotoShared;
            const custom = [ctxPhoto, ctxShared].filter((u) => u && !fail.has(u))[0] || null;
            const src = custom || (mf ? null : im.stonesExample);
            if (!src) return '';
            const oe = custom ? ` data-src="${esc(custom)}" onerror="this.onerror=null;(this.closest('configurator-modal-element,configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)"` : '';
            return `<div class="cfg__stones-photo"><img src="${esc(src)}" alt="Birthstone example"${oe}></div>`;
          })()}
          <div class="cfg__stones-stage">
            <div class="cfg__chain-clip${isNeck ? ' cfg__chain-clip--neck' : ''}"><div class="cfg__chain-scale${isNeck ? ' cfg__chain-scale--neck' : ''}">
              ${stageInner}
            </div></div>
            <span class="cfg__chain-caption">${total > 0 ? total + ' stone' + (total > 1 ? 's' : '') + ' on your ' + strand + ' · live preview' : 'Tap + below and stones appear on the ' + strand}</span>
          </div>
        </div>
        <div class="cfg__stones-row">${this.stoneCardsHtml(o, { stonePrice })}</div>
        <div class="cfg__hint cfg__hint--tiny cfg__hint--left">Up to ${maxS} stones, placed along the ${strand} ${this.ctxMulti() ? 'next to your charms' : 'with your monogram in the center'}.</div>
      </div>`}
      ${stonesOnly ? '' : `<div style="display:flex;flex-direction:column;gap:8px">
        <span class="cfg__section-label">THE REVEAL</span>
        <div class="cfg__extra-card">
          <div class="cfg__extra-head">
            <button class="cfg__extra-photo" data-action="extra-detail" data-id="scratch" aria-label="View Scratch &amp; Reveal Card details">${this.cfg.scratch.image ? `<img src="${esc(this.cfg.scratch.image)}" alt="">` : '✦'}${this.zoomIcoHtml()}</button>
            <div class="cfg__extra-body">
              <div class="cfg__extra-title-row"><span class="cfg__extra-title">Scratch &amp; Reveal Card</span><span class="cfg__pill-badge">Popular</span></div>
              <span class="cfg__extra-copy">The meaning of <i>this</i> monogram, hidden under a scratch layer.</span>
            </div>
          </div>
          <div class="cfg__extra-cta-row">
            <button class="cfg__toggle-btn" data-action="toggle-scratch" data-on="${scr}">${scr ? '✓ Added · remove' : 'Add'}</button>
            <span class="cfg__extra-price">${this.cfg.scratch.comparePrice ? `<s class="cfg__extra-was">${this.fmt(this.cfg.scratch.comparePrice)}</s> ` : ''}+${this.fmt(this.cfg.scratch.price)}</span>
          </div>
        </div>
      </div>
      <div style="display:flex;flex-direction:column;gap:8px">
        <span class="cfg__section-label">HOW IT ARRIVES</span>
        <div class="cfg__extra-card">
          <div class="cfg__extra-head">
            <button class="cfg__extra-photo" data-action="extra-detail" data-id="gift" aria-label="View Gift Box details">${this.cfg.gift.image ? `<img src="${esc(this.cfg.gift.image)}" alt="">` : '♡'}${this.zoomIcoHtml()}</button>
            <div class="cfg__extra-body">
              <span class="cfg__extra-title">Gift Box</span>
              <span class="cfg__extra-copy">Arrives in a rigid box with the meaning card inside. Ready to give.</span>
            </div>
          </div>
          <div class="cfg__extra-cta-row">
            <button class="cfg__toggle-btn" data-action="toggle-gift" data-on="${gf}">${gf ? '✓ Added · remove' : 'Add'}</button>
            <span class="cfg__extra-price">${this.cfg.gift.comparePrice ? `<s class="cfg__extra-was">${this.fmt(this.cfg.gift.comparePrice)}</s> ` : ''}+${this.fmt(this.cfg.gift.price)}</span>
          </div>
        </div>
      </div>`}
    </div>`;
  }

  /* New flow: gift box and scratch card in their own step, as in the chat. */
  giftHtml() {
    const o = this.cur(); // the second piece in mf- screens
    const scr = !!o.scratch, gf = !!o.gift;
    const card = (id, title, badge, copy, price, compare, on, action) => `<div class="cfg__extra-card">
          <div class="cfg__extra-head">
            <button class="cfg__extra-photo" data-action="extra-detail" data-id="${id}" aria-label="View ${title} details">${this.cfg[id].image ? `<img src="${esc(this.cfg[id].image)}" alt="">` : '✦'}${this.zoomIcoHtml()}</button>
            <div class="cfg__extra-body">
              <div class="cfg__extra-title-row"><span class="cfg__extra-title">${title}</span>${badge ? `<span class="cfg__pill-badge">${badge}</span>` : ''}</div>
              <span class="cfg__extra-copy">${copy}</span>
            </div>
          </div>
          <div class="cfg__extra-cta-row">
            <button class="cfg__toggle-btn" data-action="${action}" data-on="${on}">${on ? '✓ Added · remove' : 'Add'}</button>
            <span class="cfg__extra-price">${compare ? `<s class="cfg__extra-was">${this.fmt(compare)}</s> ` : ''}+${this.fmt(price)}</span>
          </div>
        </div>`;
    return `<div class="cfg__screen" style="gap:14px">
      <div class="cfg__row-title" style="padding-top:4px"><span class="cfg__row-title-text">Make it a gift <span class="cfg__optional">(optional)</span></span>${this.helpBtn('details')}</div>
      ${card('gift', 'Gift Box', '', 'Rigid box with the meaning card inside. Ready to give.', this.cfg.gift.price, this.cfg.gift.comparePrice, gf, 'toggle-gift')}
      ${card('scratch', 'Scratch &amp; Reveal Card', 'Popular', 'The meaning of <i>this</i> monogram, hidden under a scratch layer.', this.cfg.scratch.price, this.cfg.scratch.comparePrice, scr, 'toggle-scratch')}
    </div>`;
  }

  /* Arrival estimate, same count as the campaign's [arrival]. */
  arrivalLabel() {
    const d = this.campaignData() || {};
    return new Date(Date.now() + (+d.arrivalDays || 20) * 864e5).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /* The count comes from snippets/designer-stats (same number as the PDP
     Designers section); the DESIGNERS value is only a fallback. */
  designerCount(dsg) {
    const n = (this.cfg.designerCounts || {})[dsg.name.toLowerCase()];
    return typeof n === 'number' && n > 0 ? n : dsg.count;
  }

  reviewHtml() {
    return `<div class="cfg__screen" style="gap:14px">
      ${this.revHeadHtml(true, this.revEditorHtml())}
      ${this.designerCardHtml()}
    </div>`;
  }

  revEditorHtml() {
    const S = this.state;
    const nst = this.stoneCount(S);
    return S.revEditOpen ? `<div class="cfg__rev-editor">
      ${this.cfg.multiCharm ? this.charmFieldsHtml(S, 'cname', 'select-charms', this.cfg.maxCharms) : `<div class="cfg__rev-field"><span class="cfg__rev-label">NAME</span>
        <input class="cfg__input cfg__input--small${S.nameText.trim() && !this.nameValid() ? ' cfg__input--invalid' : ''}" data-input="name" value="${esc(S.nameText)}" autocomplete="off">
        <div data-region="name-msgs">${this.nameMsgsHtml()}</div>
      </div>`}
      <div class="cfg__rev-field"><span class="cfg__rev-label">STYLE</span>
        <div class="cfg__rev-row">${STYLES.map((s) => `<button class="cfg__rev-chip cfg__rev-chip--style" data-on="${S.style === s.id}" data-action="select-style" data-id="${s.id}" aria-label="${s.name}${S.style === s.id ? ', selected' : ''}">${this.styleThumbHtml(s, S.finish)}<span>${s.name}</span></button>`).join('')}</div>
      </div>
      <div class="cfg__rev-field"><span class="cfg__rev-label">FINISH</span>
        <div class="cfg__rev-swatches">${this.finishList(this.cfg.finishIds).map((f) => `<button class="cfg__rev-swatch" data-on="${S.finish === f.id}" data-action="select-finish" data-id="${f.id}" style="background:${f.grad}" aria-label="${f.name}${S.finish === f.id ? ', selected' : ''}"></button>`).join('')}</div>
      </div>
      ${this.cfg.hasCord ? `<div class="cfg__rev-field"><span class="cfg__rev-label">CORD</span>
        <div class="cfg__rev-swatches">${(this.cfg.cords && this.cfg.cords.length ? this.cfg.cords : CORDS).map((cd) => `<button class="cfg__rev-swatch" data-on="${S.cord === cd.id}" data-action="select-cord" data-id="${cd.id}" style="background:${cd.c}" aria-label="${cd.name} cord${S.cord === cd.id ? ', selected' : ''}"></button>`).join('')}</div>
      </div>` : ''}
      ${this.cfg.hasLength ? `<div class="cfg__rev-field"><span class="cfg__rev-label">LENGTH</span>
        <div class="cfg__rev-row">${(this.cfg.lengths && this.cfg.lengths.length ? this.cfg.lengths : [16, 18, 20, 22]).map((L) => `<button class="cfg__rev-chip cfg__rev-chip--len" data-on="${S.length === L}" data-action="select-length" data-n="${L}" aria-label="${L} inches${S.length === L ? ', selected' : ''}">${L}″</button>`).join('')}</div>
      </div>` : ''}
      ${this.cfg.hideStones ? '' : `<div class="cfg__rev-field">
        <div class="cfg__section-label-row"><span class="cfg__rev-label" style="flex:1">BIRTHSTONES</span><span style="font-size:12px;color:#6C6A6B">${nst} of ${this.cfg.maxStones || 6}</span></div>
        <div class="cfg__stones-row" style="padding:2px;margin:0 -2px">${this.stoneCardsHtml(S, { mini: true })}</div>
      </div>`}
      <div class="cfg__rev-row">
        <button class="cfg__rev-chip" data-on="${S.scratch}" data-action="toggle-scratch" style="font-size:13px">Scratch card · ${this.fmt(this.cfg.scratch.price)}</button>
        <button class="cfg__rev-chip" data-on="${S.gift}" data-action="toggle-gift" style="font-size:13px">Gift box · ${this.fmt(this.cfg.gift.price)}</button>
      </div>
      <span class="cfg__rev-note">Edits save automatically. The total below updates live.</span>
    </div>` : '';
  }

  revHeadHtml(withTitle, editor) {
    const S = this.state;
    const fin = this.finishOf(S.finish);
    const sty = this.styleOf(S.style);
    const exParts = [];
    const nst = this.stoneCount(S);
    if (nst) exParts.push(nst + ' birthstone' + (nst > 1 ? 's' : ''));
    if (S.scratch) exParts.push('scratch card');
    if (S.gift) exParts.push('gift box');
    const spec = [];
    if (this.cfg.multiCharm) spec.push(this.charmsLabel(S));
    if (S.length) spec.push(S.length + '″');
    if (S.cord) spec.push(this.cordOf(S.cord, this.cfg.cords).name + ' cord');
    spec.push(fin.name);
    if (sty) spec.push(sty.name);
    return `<div class="cfg__review-card">
        <div class="cfg__review-head">
          ${this.cfg.images.hero
            ? `<div class="cfg__review-thumb"><img src="${esc(this.cfg.images.hero)}" alt="${esc(this.cfg.product.title)}"></div>`
            : `<div class="cfg__disc" style="background:${fin.grad}"><div class="cfg__disc-ring" style="border-color:${fin.ring}"><span class="cfg__disc-ini cfg-serif" style="color:${fin.letter};font-size:${this.initialsOf(S.nameText).length > 2 ? 10 : 12}px">${esc(this.initialsOf(S.nameText))}</span></div></div>`}
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px">
            <span class="cfg__review-names cfg-serif">${esc(this.namesDisplayOf(S.nameText))}</span>
            <span class="cfg__review-sub">${withTitle ? esc(this.cfg.product.title) + ' · ' : ''}${esc(spec.join(' · '))}</span>
            ${withTitle || exParts.length ? `<span class="cfg__review-sub">${exParts.length ? esc(exParts.join(' · ')) : 'No extras'}</span>` : ''}
          </div>
          <button class="cfg__link-btn" data-action="toggle-rev-edit" aria-label="${S.revEditOpen ? 'Close editor' : 'Edit your design'}">${S.revEditOpen ? 'Close' : 'Edit'}</button>
        </div>
        ${editor}
      </div>`;
  }

  designerCardHtml(opts) {
    const noHead = !!(opts && opts.noHead); // short review: photo and name already sit above
    const S = this.state;
    const dsg = DESIGNERS[(S.designerIdx || 0) % DESIGNERS.length];
    const dsgImg = (this.cfg.images.designers || {})[dsg.name.toLowerCase()];
    const timeline = [
      dsg.name + ' emails your draft in 24 to 72 hours', 'Request changes, unlimited', 'You approve, then we craft it', 'Delivered 15 to 20 days after your order',
    ];
    return `<div class="cfg__designer-card">
        ${noHead ? '' : `<span class="cfg__section-label">YOUR DESIGNER</span>
        <div class="cfg__designer-head">
          <div class="cfg__designer-col">
            <div class="cfg__designer-photo">${dsgImg ? `<img src="${esc(dsgImg)}" alt="${dsg.name}">` : `<span class="cfg-serif" style="color:#C9A66E">${dsg.name[0]}</span>`}</div>
            <div class="cfg__designer-rating cfg__designer-rating--under"><span class="cfg__designer-stars">★★★★★</span><b>${dsg.rating.toFixed(1)}</b></div>
          </div>
          <div class="cfg__designer-info">
            <span class="cfg__designer-assigned cfg-serif">We've assigned ${dsg.name} to your project</span>
            <span class="cfg__designer-sub">She'll personally draw your monogram and follow your order through to delivery.</span>
            <span class="cfg__designer-count">${this.designerCount(dsg).toLocaleString('en-US')} monograms designed</span>
          </div>
        </div>`}
        <div class="cfg__designer-quote">
          <span class="cfg__designer-quote-text">“${esc(dsg.quote)}”</span>
          <div class="cfg__designer-quote-by"><b>${dsg.reviewer}</b><span style="color:#6C6A6B">· Verified buyer</span></div>
        </div>
        <span style="font-size:14px;font-weight:600;margin-top:2px">Once you complete your order:</span>
        <div class="cfg__timeline">${timeline.map((t, i) => `<div class="cfg__timeline-row"><span class="cfg__timeline-num">${i + 1}</span><span class="cfg__timeline-text">${t}</span></div>`).join('')}</div>
        <div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><b>Nothing is produced without your approval</b></div>
        <div class="cfg__guarantee"><svg width="16" height="16" viewBox="0 0 20 20" fill="none" style="flex-shrink:0"><path d="M10 1.5l7 2.5v5c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V4l7-2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M6.8 9.8l2.2 2.2 4.2-4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Not happy? Unlimited revisions or a full refund.</span></div>
        <span class="cfg__trust-line">${esc(this.cfg.trustLine || '')}</span>
      </div>`;
  }

  /* New flow: short review. Design, the two gift extras one tap away, three
     promises, the designer's story folded; the button carries price and date. */
  reviewNewHtml() {
    const S = this.state;
    const dsg = DESIGNERS[(S.designerIdx || 0) % DESIGNERS.length];
    const quick = (on, action, label, price) => `<button class="cfg__quick-btn" data-on="${on}" data-action="${action}">${on ? '✓ ' + label : '+ ' + label + ' ' + this.fmt(price)}</button>`;
    const dsgImg = (this.cfg.images.designers || {})[dsg.name.toLowerCase()];
    const checks = ['Your draft by email in 24 to 72 hours', 'Unlimited changes, or a full refund', 'Arrives around ' + this.arrivalLabel()];
    return `<div class="cfg__screen" style="gap:14px">
      <div class="cfg__row-title" style="padding-top:4px"><span class="cfg__row-title-text">Your design</span></div>
      ${this.revHeadHtml(false, this.revEditorHtml())}
      <div class="cfg__quick-row">
        ${quick(!!S.gift, 'toggle-gift', 'Gift box', this.cfg.gift.price)}
        ${quick(!!S.scratch, 'toggle-scratch', 'Scratch card', this.cfg.scratch.price)}
      </div>
      <div class="cfg__dsg-strip">
        <div class="cfg__dsg-strip-photo">${dsgImg ? `<img src="${esc(dsgImg)}" alt="${esc(dsg.name)}">` : `<span class="cfg-serif">${esc(dsg.name[0])}</span>`}</div>
        <div class="cfg__dsg-strip-info">
          <span class="cfg__dsg-strip-name cfg-serif">${esc(dsg.name)} draws your monogram</span>
          <span class="cfg__dsg-strip-meta"><span class="cfg__designer-stars">★★★★★</span> <b>${dsg.rating.toFixed(1)}</b> · ${this.designerCount(dsg).toLocaleString('en-US')} monograms</span>
        </div>
      </div>
      <ul class="cfg__checks">${checks.map((c) => `<li><span class="cfg__checks-tick" aria-hidden="true">✓</span><span>${esc(c)}</span></li>`).join('')}</ul>
      <button class="cfg__how-toggle" data-action="toggle-how" aria-expanded="${!!S.howOpen}">How ${esc(dsg.name)} works <span aria-hidden="true">${S.howOpen ? '▾' : '▸'}</span></button>
      ${S.howOpen ? this.designerCardHtml({ noHead: true }) : ''}
    </div>`;
  }

  dealHtml() {
    const S = this.state;
    const bundle = this.cfg.bundle;
    const pieces = S.pieces.map((p, i) => {
      const cf = this.finishOf(p.cfg.finish);
      const stN = (this.styleOf(p.cfg.style) || {}).name || '';
      const openEd = !!(S.inlineEdit && S.inlineEdit.idx === i);
      return `<div class="cfg__piece-card" data-open="${openEd}">
        <div class="cfg__piece-head">
          ${(() => {
            const pimg = p.offerHandle ? (this.offerOf(p.offerHandle) || {}).image : this.cfg.images.hero;
            return pimg
              ? `<div class="cfg__piece-thumb"><img src="${esc(pimg)}" alt="${esc(p.kind)}"></div>`
              : `<div class="cfg__piece-disc" style="background:${cf.grad}"><span class="cfg-serif" style="color:${cf.letter}">${esc(this.initialsOf(p.cfg.nameText))}</span></div>`;
          })()}
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:1px"><span class="cfg__piece-title">${esc(p.kind)}</span><span class="cfg__piece-name cfg-serif">${esc(this.namesDisplayOf(p.cfg.nameText))}</span></div>
        </div>
        <div class="cfg__piece-foot"><span class="cfg__piece-sub">${p.cfg.charmCount ? this.charmsLabel(p.cfg) + ' · ' : ''}${esc(stN)} · ${cf.name}${p.cfg.length ? ' · ' + p.cfg.length + '″' : ''}</span><button class="cfg__link-btn" style="padding:4px 0;font-size:13px" data-action="edit-piece" data-i="${i}" aria-label="${openEd ? 'Close editor for' : 'Edit'} ${esc(p.kind)}">${openEd ? 'Close' : 'Edit'}</button></div>
      </div>`;
    }).join('');
    const units = S.pieces.length;
    const camp = this.campaign();
    const shown = camp ? this.campOfferIdx() : null;
    const offers = (this.cfg.offers || []).map((o, i) => (shown && shown.indexOf(i) === -1 ? '' : `
      <div class="cfg__offer-card">
        <div class="cfg__offer-head">
          <div class="cfg__offer-photo">${o.image ? `<img src="${esc(o.image)}" alt="${esc(o.title)}">` : ''}</div>
          <div class="cfg__offer-body">
            <div class="cfg__offer-title-row"><span class="cfg__offer-title">${esc(o.title)}</span>${i === 0 ? '<span class="cfg__pill-badge">Popular</span>' : ''}</div>
            <span class="cfg__offer-sub">Any name · same or new design</span>
            <div class="cfg__offer-price-row"><span class="cfg__offer-deal-price">${this.fmt(o.dealPrice)} with bundle</span><span class="cfg__offer-was">was ${this.fmt(o.price)}</span></div>${this.campSaveHtml(o, camp)}
          </div>
        </div>
        <button class="cfg__offer-cta" data-action="start-mf" data-i="${i}">Customize</button>
      </div>`)).join('');
    return `<div class="cfg__screen" style="gap:14px;padding-top:14px">
      <div style="display:flex;flex-direction:column;gap:8px">
        <span class="cfg__deal-label">YOUR CUSTOMIZED PIECES</span>
        <div class="cfg__pieces-row">${pieces}</div>
        ${this.inlineEditorHtml()}
      </div>
      ${units >= 2 ? `<div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><b>Bundle unlocked: prices below include your discount</b></div>` : ''}
      ${this.cfg.offers && this.cfg.offers.length && camp ? this.campListHtml(camp, offers) : ''}
      ${this.cfg.offers && this.cfg.offers.length && !camp ? `
      <div class="cfg__bundle-hero">
        <span class="cfg__bundle-kicker">BUNDLE UNLOCKED</span>
        <div class="cfg__bundle-price-row">
          <span class="cfg__bundle-price cfg-serif">${this.fmt(bundle.secondPrice)}</span>
          <span class="cfg__bundle-was">${this.fmt(this.cfg.product.price)}</span>
          <span class="cfg__bundle-save">SAVE ${Math.round(bundle.savings / Math.max(1, this.cfg.product.price) * 100)}%</span>
        </div>
        <span class="cfg__bundle-copy">Any name, any metal, at the bundle price.</span>
      </div>
      ${offers}
      <div class="cfg__urgency"><svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="#8A6A2E" stroke-width="1.3"/><path d="M7 3.5V7l2.5 1.5" stroke="#8A6A2E" stroke-width="1.3" stroke-linecap="round"/></svg><span>${esc(this.cfg.urgency)}</span></div>` : ''}
    </div>`;
  }

  /* Classic deal screen during a campaign: the gift list replaces the
     "Bundle unlocked" box, the offers follow the chosen person. */
  campListHtml(c, offersHtml) {
    const dark = this.campDark(c);
    const hero = dark
      ? `<div class="cfg-cm-deal${c.look === 'deal' ? ' cfg-cm--deal' : ''}">
          <div class="cfg-cm-deal__top"><span class="cfg-cm__kicker">${esc(c.listKicker || c.kicker || '')}</span>${this.campClockHtml(c)}</div>
          ${c.listHero ? `<span class="cfg-cm-deal__hero cfg-serif">${this.campText(c.listHero, c)}</span>` : ''}
          ${c.listSmall ? `<span class="cfg-cm-deal__small">${this.campText(c.listSmall, c)}</span>` : ''}
        </div>`
      : (c.listHero ? `<span class="cfg-cm-list__hero cfg-serif">${this.campText(c.listHero, c)}</span>` : '');
    return `<div class="cfg-cm-list">
        ${!dark && c.listKicker ? `<span class="cfg-cm__kicker">${esc(c.listKicker)}</span>` : ''}
        ${c.listTitle ? `<span class="cfg-cm-list__title cfg-serif">${this.campText(c.listTitle, c)}</span>` : ''}
        ${this.campPeopleHtml()}
        ${hero}
      </div>
      ${offersHtml}
      ${c.listFoot ? `<div class="cfg__urgency"><svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="#8A6A2E" stroke-width="1.3"/><path d="M7 3.5V7l2.5 1.5" stroke="#8A6A2E" stroke-width="1.3" stroke-linecap="round"/></svg><span>${this.campText(c.listFoot, c)}</span></div>` : ''}`;
  }

  /* ---------- footer ---------- */
  footerHtml() {
    const S = this.state, mf = this.mfMode();
    const stepIdx = this.STEPS.findIndex((s) => s.id === S.screen);
    let total = 0, caption = '', canBk = false, rows = [], primary = 'Next step', primaryAction = 'next', disabled = false, helper = '', note = '', showBack = stepIdx > 0, backText = 'Back', backAction = 'back';
    const bd = (o, base, label, stonePrice) => {
      const r = [{ l: label, v: this.fmt(base) }];
      const cx = this.charmExtra(o); if (cx) r.push({ l: 'Extra charms × ' + ((o.charmCount || 1) - 1), v: this.fmt(cx) });
      const n = this.stoneCount(o); if (n) r.push({ l: 'Birthstone × ' + n, v: this.fmt(n * (stonePrice || this.cfg.stonePrice)) });
      if (o.scratch) r.push({ l: 'Scratch & Reveal Card', v: this.fmt(this.cfg.scratch.price) });
      if (o.gift) r.push({ l: 'Gift Box', v: this.fmt(this.cfg.gift.price) });
      return r;
    };
    if (stepIdx >= 0 || S.screen === 'review') {
      total = this.cfg.product.price + this.addonPrice(S);
      caption = 'Updates as you customize';
      canBk = S.screen === 'details' || S.screen === 'gift' || (S.screen === 'review' && !this.newFlow);
      rows = bd(S, this.cfg.product.price, this.cfg.product.title);
      if (S.screen === 'name') { disabled = !this.nameValid(); if (this.cfg.multiCharm && disabled && !this.charmMsgsHtml(S)) helper = 'Type a name for each charm to continue'; else if (!S.nameText.trim()) helper = 'Type a name to continue'; else if (disabled) helper = 'Fix the highlighted text to continue'; }
      if (S.screen === 'style') { disabled = !S.style; if (disabled) helper = 'Choose a style to continue'; }
      if (S.screen === 'details') { primary = S.editReturn ? 'Save & return' : 'Next step'; showBack = !S.editReturn; }
      else if (S.screen === 'gift') { primary = S.editReturn ? 'Save & return' : (S.gift || S.scratch ? 'Next step' : 'Continue without'); showBack = !S.editReturn; }
      else if (S.editReturn) { primary = 'Save & return'; showBack = false; }
      if (S.screen === 'review') {
        primary = S.editingPieceIdx != null || S.cartEdit ? 'Save changes' : 'Add to cart';
        primaryAction = 'primary-review'; showBack = false;
        disabled = !this.nameValid() || !S.style;
        if (disabled) helper = 'Fix the name to continue';
        if (this.newFlow) {
          if (primary === 'Add to cart') primary = 'Add to cart · ' + this.fmt(total);
          note = 'Arrives around ' + this.arrivalLabel() + ' · nothing is made before you approve';
        }
      }
    }
    if (mf) {
      const offer = this.offerOf(S.mfOffer) || { price: 0 };
      const mfBase = offer.dealPrice != null ? offer.dealPrice : offer.price;
      total = mfBase + this.addonPrice(S.mf || {}, { stonePrice: offer.stonePrice });
      caption = 'Bundle price for this piece';
      showBack = true; backAction = 'mf-back'; backText = 'Back';
      canBk = S.screen === 'mf-details' || S.screen === 'mf-gift';
      rows = bd(S.mf || {}, mfBase, (offer.title || 'Piece') + ' · bundle', offer.stonePrice);
      if (S.screen === this.mfSteps().slice(-1)[0]) { primary = S.editingPieceIdx != null ? 'Save changes' : 'Add to cart'; primaryAction = 'mf-add'; }
      else { primary = 'Next step'; primaryAction = 'mf-next'; }
      if (S.screen === 'mf-name') { disabled = !this.mfNameValid(); const t = ((S.mf || {}).nameText || '').trim(); if (this.ctxMulti() && disabled && !this.charmMsgsHtml(S.mf || {})) helper = 'Type a name for each charm to continue'; else if (!t) helper = S.pieces.length ? 'Tap a saved name, or type a new one' : 'Type a name to continue'; else if (disabled) helper = 'Fix the highlighted text to continue'; }
      if (S.screen === 'mf-style') { disabled = !(S.mf || {}).style; if (disabled) helper = 'Choose a style to continue'; if (this.mfSteps().indexOf('mf-style') === -1) primary = 'Save style'; }
    }
    if (S.screen === 'deal') {
      // Final prices, as the cart will show them: pieces added through the
      // bundle offers are listed at their discounted price and flagged.
      const pieceBase = (p) => {
        const o = p.offerHandle ? this.offerOf(p.offerHandle) : null;
        const base = o && o.dealPrice != null ? o.dealPrice : p.base;
        return { base, discounted: base < p.base };
      };
      total = S.pieces.reduce((a, p) => a + pieceBase(p).base + this.addonPrice(p.cfg, { stonePrice: p.stonePrice }), 0);
      caption = '';
      canBk = true;
      rows = S.pieces.map((p) => {
        const pb = pieceBase(p);
        const n = this.stoneCount(p.cfg) + (p.cfg.scratch ? 1 : 0) + (p.cfg.gift ? 1 : 0);
        const tags = [];
        if (pb.discounted) tags.push('bundle');
        if (n) tags.push('+' + n + ' add-on' + (n > 1 ? 's' : ''));
        return { l: p.kind + (tags.length ? ' (' + tags.join(' ') + ')' : ''), v: this.fmt(pb.base + this.addonPrice(p.cfg, { stonePrice: p.stonePrice })) };
      });
      primary = 'Go to cart · ' + S.pieces.length + (S.pieces.length > 1 ? ' pieces' : ' piece');
      primaryAction = 'go-cart'; showBack = false;
    }
    const busy = S.busy;
    return `
      ${S.breakdownOpen && canBk ? `<div class="cfg__breakdown">${rows.map((r) => `<div class="cfg__breakdown-row"><span>${esc(r.l)}</span><span>${r.v}</span></div>`).join('')}</div>` : ''}
      ${S.screen === 'review' && this.newFlow && !mf ? '' : canBk ? `<button class="cfg__price-btn" data-action="toggle-breakdown" aria-label="Price breakdown">
        <span class="cfg__price-total cfg-serif">${this.fmt(total)}</span>${caption ? `<span class="cfg__price-caption">${esc(caption)}</span>` : '<span style="flex:1"></span>'}
        <svg class="cfg__price-chev${S.breakdownOpen ? ' cfg__price-chev--flip' : ''}" width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5l5 5 5-5" stroke="#A8ADB3" stroke-width="1.6" stroke-linecap="round"/></svg>
      </button>` : `<div class="cfg__price-plain"><span class="cfg__price-total cfg-serif">${this.fmt(total)}</span>${caption ? `<span class="cfg__price-caption">${esc(caption)}</span>` : ''}</div>`}
      ${helper ? `<span class="cfg__footer-helper">${esc(helper)}</span>` : ''}
      <div class="cfg__footer-btns">
        ${showBack ? `<button class="cfg__btn-back" data-action="${backAction}">${esc(backText)}</button>` : ''}
        <button class="cfg__btn-primary${busy ? ' is-loading' : ''}" data-action="${primaryAction}" ${disabled || busy ? 'disabled' : ''}>${busy ? 'Adding…' : esc(primary)}</button>
      </div>
      ${note ? `<span class="cfg__footer-note">${esc(note)}</span>` : ''}`;
  }

  sheetTabsHtml(labels) {
    const cur = this.state.helpTab || 0;
    return `<div class="cfg__sheet-tabs">${labels.map((l, i) => `<button class="cfg__sheet-tab" data-on="${i === cur}" data-action="help-tab" data-tab="${i}">${l}</button>`).join('')}</div>`;
  }

  lengthHelpHtml() {
    const tab = this.state.helpTab || 0;
    const imgs = this.cfg.images || {};
    let body = '';
    if (tab === 0) {
      const rows = [['16 in', '40 cm'], ['18 in', '45 cm'], ['20 in', '50 cm'], ['22 in', '55 cm']];
      body = `${imgs.sizeChart ? `<div class="cfg__sheet-photo" style="height:180px"><img src="${esc(imgs.sizeChart)}" alt="Chain length guide"></div>` : ''}
        <div class="cfg__ht-table">
          <div class="cfg__ht-row cfg__ht-row--head"><span>Length</span><span>Inches</span><span>Centimeters</span></div>
          ${rows.map((r, i) => `<div class="cfg__ht-row"><span>${['Short', 'Classic', 'Relaxed', 'Long'][i]}</span><span>${r[0]}</span><span>${r[1]}</span></div>`).join('')}
        </div>`;
    } else if (tab === 1) {
      const entries = [
        ['14″', 'Sits high on the neck: choker length. Style solo or layer with longer necklaces for a statement stack.'],
        ['16″', 'Falls at the base of the neck. One of our most common lengths, and a versatile way to start a collection.'],
        ['18″', 'Falls on or just below the base of the neck. Perfect for pairing with charms and pendants.'],
        ['20 to 22″', 'Sits just above the chest. Plays perfectly with open necklines and layered stacks.'],
      ];
      body = `<div class="cfg__sheet-lines">${entries.map((e) => `<div class="cfg__sheet-line"><span class="cfg__sheet-lead" style="flex-shrink:0;min-width:52px">${e[0]}</span><span>${e[1]}</span></div>`).join('')}</div>`;
    } else {
      const rows = [["Women's necklaces", '0.6 in'], ["Men's necklaces", '0.8 in'], ['Bracelets', '0.8 in'], ['Keychains', '0.98 in']];
      body = `${imgs.charmWidth ? `<div class="cfg__sheet-photo" style="height:180px"><img src="${esc(imgs.charmWidth)}" alt="Charm width guide"></div>` : ''}
        <div class="cfg__ht-table">
          <div class="cfg__ht-row cfg__ht-row--head"><span>Piece</span><span></span><span>Charm width</span></div>
          ${rows.map((r) => `<div class="cfg__ht-row"><span>${r[0]}</span><span></span><span>${r[1]}</span></div>`).join('')}
        </div>
        <div class="cfg__sheet-line" style="font-size:13px;color:#565356">For scale: a dime is 0.7 in across, a quarter just under 1 in.</div>`;
    }
    return this.sheetTabsHtml(['Size chart', 'How to style', 'Charm width']) + body;
  }

  nameHelpHtml() {
    const tab = this.state.helpTab || 0;
    const imgs = this.cfg.images || {};
    const previews = (list) => `<div class="cfg__previews">${[0, 1, 2].map((i) => (list && list[i])
      ? `<img class="cfg__preview-img" src="${esc(list[i])}" alt="Monogram example">`
      : '<div class="cfg__preview-ph"></div>').join('')}</div>`;
    let body;
    if (tab === 0) {
      body = `${previews(imgs.nameLong)}
        <div class="cfg__sheet-line"><span>Enter as many letters as you like: there's no limit. Pro tip: monograms look their best under about 10 characters (less is more). The letters aren't typed, they're intertwined by hand, so even long names become one balanced symbol.</span></div>`;
    } else {
      body = `${previews(imgs.nameSame)}
        <div class="cfg__sheet-line"><span>Repeated initials are one of our favorite requests: the artist plays with rotation, size and interlocking lines to build a symmetrical crest. Think family crest, not text. If it feels too repetitive, we'll tweak it until you love it.</span></div>`;
    }
    return this.sheetTabsHtml(['Long names', 'Same initials']) + body +
      `<div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><b>Your approval is always required before production starts.</b></div>`;
  }

  /* ---------- sheets ---------- */
  sheetsHtml() {
    const S = this.state;
    const any = S.helpSheet || S.exitSheet || S.zoomStyle || S.extraSheet;
    if (!any) return '';
    let inner = '';
    if (S.helpSheet) {
      let body;
      if (S.helpSheet === 'length') body = this.lengthHelpHtml();
      else if (S.helpSheet === 'name') body = this.nameHelpHtml();
      else body = `<div class="cfg__sheet-lines">${(HELP[S.helpSheet] || []).map((h) => `<div class="cfg__sheet-line">${h.b ? '<span class="cfg__sheet-bullet">•</span>' : ''}<span>${h.lead ? `<span class="cfg__sheet-lead">${h.lead}</span>` : ''}${h.t}</span></div>`).join('')}</div>`;
      inner = `<div class="cfg__sheet-title-row"><span class="cfg__sheet-title">How it works</span><button class="cfg__sheet-close" data-action="close-sheets" aria-label="Close">✕</button></div>
        ${body}
        <button class="cfg__sheet-btn-outline" data-action="close-sheets">Got it</button>`;
    } else if (S.exitSheet) {
      inner = `<div class="cfg__exit-copy"><span class="cfg__exit-title cfg-serif">Keep your design?</span><span class="cfg__exit-sub">We'll hold it while you look around.</span></div>
        <div class="cfg__exit-btns">
          <button class="cfg__exit-discard" data-action="discard-exit">Discard</button>
          <button class="cfg__exit-keep" data-action="keep-exit">Keep &amp; exit</button>
        </div>`;
    } else if (S.zoomStyle) {
      const s = STYLES.find((x) => x.id === S.zoomStyle);
      inner = `<div style="display:flex;flex-direction:column;gap:4px"><span class="cfg__sheet-title--serif cfg-serif" style="font-size:18px">${s.name}</span><span style="font-size:13.5px;color:#5E656C">Examples of this style. Your design will use your own letters.</span></div>
        <div class="cfg__zoom-exs">${this.styleExHtml(s, true)}</div>
        <button class="cfg__sheet-btn-solid" data-action="zoom-pick">Choose this style</button>`;
    } else if (S.extraSheet) {
      const ex = S.extraSheet === 'scratch'
        ? { name: 'Scratch & Reveal Card', img: this.cfg.scratch.image, price: this.cfg.scratch.price, on: !!this.cur().scratch, long: 'A printed card that hides the meaning of this monogram under a scratch layer: they reveal it letter by letter.', details: ['Personalized to the letters on this piece', 'Foil scratch layer, coin friendly', 'Fits inside the gift box'] }
        : { name: 'Gift Box', img: this.cfg.gift.image, price: this.cfg.gift.price, on: !!this.cur().gift, long: 'A rigid keepsake box with the meaning card tucked inside, ready to hand over as is.', details: ['Rigid box with soft insert', 'Meaning card included', 'No prices anywhere inside'] };
      inner = `<div class="cfg__sheet-title-row"><span class="cfg__sheet-title--serif cfg-serif" style="flex:1;font-size:18px">${ex.name}</span><button class="cfg__sheet-close" data-action="close-sheets" aria-label="Close">✕</button></div>
        ${ex.img ? `<div class="cfg__sheet-photo cfg__sheet-photo--contain"><img src="${esc(ex.img)}" alt="${ex.name}"></div>` : ''}
        <span style="font-size:14.5px;line-height:1.55;color:#5E656C">${ex.long}</span>
        <div class="cfg__sheet-lines">${ex.details.map((d) => `<div class="cfg__sheet-line" style="font-size:14px"><span class="cfg__sheet-bullet">•</span><span>${d}</span></div>`).join('')}</div>
        <div class="cfg__sheet-price-row">
          <span class="cfg__sheet-price cfg-serif">${this.fmt(ex.price)}</span>
          <button class="cfg__sheet-cta" data-action="extra-sheet-toggle" data-on="${ex.on}">${ex.on ? '✓ Added · remove' : 'Add to this piece'}</button>
        </div>`;
    }
    return `<div class="cfg__sheet-backdrop" data-action="sheet-backdrop"><div class="cfg__sheet">
      <div class="cfg__sheet-handle"></div>${inner}
    </div></div>`;
  }
}

if (!customElements.get('configurator-modal-element')) {
  customElements.define('configurator-modal-element', ConfiguratorModal);
}

/* Shared engine for sibling experiences (customizer-chat extends this class). */
window.CharmlryConfigurator = { ConfiguratorModal, FINISHES, STYLES, CORDS, DESIGNERS, MONO, STONE_META, esc, CartAddEvent, chatWanted };
